// src/Pages/Admin/pacientes.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useAuth } from "../../context/AuthContext";
import { db, storage } from "../../firebase/config";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  deleteField,
} from "firebase/firestore";

import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  Search,
  UserCircle2,
  Activity,
  FileText,
  Upload,
  Save,
  Loader2,
} from "lucide-react";

import { initializeApp, getApps, deleteApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signOut,
} from "firebase/auth";

import { firebaseConfig } from "../../firebase/config";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";


const H1 = "text-2xl font-extrabold text-slate-900";
const H2 = "text-lg font-semibold text-slate-900";
const TEXT = "text-sm text-slate-700";
const MUTED = "text-xs text-slate-500";

const CARD = "bg-gray border border-gray-200/70 rounded-2xl shadow-sm";
const SUBCARD = "bg-gray-100 border border-gray-200/70 rounded-2xl shadow-sm";

const INPUT =
  "mt-1 w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-sm " +
  "placeholder:text-slate-400 hover:border-slate-400 " +
  "focus:outline-none focus:ring-4 focus:ring-blue-500/15 focus:border-blue-500";

const BTN_PRIMARY =
  "inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl " +
  "font-semibold hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-500/20 disabled:opacity-60";

const BTN_SECONDARY =
  "inline-flex items-center justify-center gap-2 bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded-xl " +
  "font-semibold hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-500/10 disabled:opacity-60";

const TAB_BASE =
  "px-3 py-1.5 rounded-full text-xs sm:text-sm whitespace-nowrap border transition font-semibold";
const TAB_ACTIVE = "bg-blue-600 text-white border-blue-600 shadow-sm";
const TAB_IDLE = "bg-white text-slate-700 border-slate-300 hover:bg-slate-50";


const clp = (n) =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(n || 0);

function useDebounced(value, delay = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const normEmail = (v) => (v || "").toString().toLowerCase().trim();

function buildPacienteKeys(u) {
  const keys = new Set();

  // id del doc en /usuarios
  if (u?.id) keys.add(String(u.id));

  // campos que a veces guardas
  if (u?.uid) keys.add(String(u.uid));
  if (u?.uidAuth) keys.add(String(u.uidAuth));

  // correos
  const c1 = normEmail(u?.correo);
  const c2 = normEmail(u?.email);
  if (c1) keys.add(c1);
  if (c2) keys.add(c2);
  if (String(u?.id || "").includes("@")) keys.add(normEmail(u.id));

  return keys;
}

function buildRolesSet(pacientesUIDs) {
  return new Set(
    (pacientesUIDs || []).map((x) =>
      String(x).includes("@") ? normEmail(x) : String(x)
    )
  );
}

function isPaciente(u, rolesSet) {
  const keys = buildPacienteKeys(u);
  for (const k of keys) {
    if (rolesSet.has(k)) return true;
  }
  return false;
}

function hasValidCorreo(u) {
  const correo = normEmail(
    u?.correo || u?.email || (String(u?.id || "").includes("@") ? u.id : "")
  );
  return !!correo && correo.includes("@");
}

function loadCorreoKey(u) {
  return normEmail(
    u?.correo || u?.email || (String(u?.id || "").includes("@") ? u.id : "")
  );
}

function makeSetNormalized(ids = []) {
  return new Set(
    (ids || []).map((x) =>
      String(x).includes("@") ? normEmail(x) : String(x)
    )
  );
}

function isBlockedByRole(u, blockedSet) {
  const keys = buildPacienteKeys(u);
  for (const k of keys) {
    if (blockedSet.has(k)) return true;
  }
  return false;
}

function isAutoPaciente(u, blockedSet) {
  if (!hasValidCorreo(u)) return false;
  if (isBlockedByRole(u, blockedSet)) return false;
  return true;
}

function isPacienteFull(u, rolesUserSet, blockedSet) {
  if (isPaciente(u, rolesUserSet)) return true;
  return isAutoPaciente(u, blockedSet);
}


const EVAL_INICIAL_EMPTY = {
  antecedentesClinicos: "",
  diagnostico: "",
  historiaProblema: "",
  habitos: "",
  sueno: "",
  alimentacionHidratacion: "",
  examenObservacion: "",
  examenPalpacion: "",
  examenROM: "",
  examenFuerza: "",
  examenPruebasEspecificas: "",
  objetivoPaciente: "",
  objetivoTerapeutico: "",
  planTratamiento: "",
};

const ALTA_EMPTY = {
  fechaAlta: "",      
  evolucion: "",
  objetivosAlcanzados: "",
  examenFinalObservacion: "",
  examenFinalPalpacion: "",
  examenFinalROM: "",
  examenFinalFuerza: "",
  examenFinalPruebasEspecificas: "",
  recomendaciones: "",
};

async function exportNodeToPDF(node, filename = "ficha.pdf", opts = {}) {
  if (!node) return;

  const { paciente, profesionalNombre, direccionEkine } = opts;

  const orientation = opts?.orientation || "p"; // "p" portrait, "l" landscape
  const format = opts?.paper || "a4";           // "a4" o "letter"
  const pdf = new jsPDF({ orientation, unit: "mm", format });
  const margin = 12;
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const usableW = pageW - margin * 2;
  const usableH = pageH - margin * 2;

  // ===== Wrapper visible (opacity 0) para que html2canvas lo capture =====
  const wrapper = document.createElement("div");
  wrapper.className = "pdf-mode";
  wrapper.style.position = "fixed";
  wrapper.style.left = "-99999px";
  wrapper.style.top = "0";
  wrapper.style.width = "794px"; // base estable
  wrapper.style.padding = "24px";
  wrapper.style.boxSizing = "border-box";
  wrapper.style.background = "#fff";
  wrapper.style.opacity = "1";        // invisible, pero renderizable
  wrapper.style.visibility = "visible";
  wrapper.style.pointerEvents = "none";
  wrapper.style.zIndex = "999999";      // importante: no -1
  document.body.appendChild(wrapper);

  // Espera fonts (cuando aplica)
  try {
    if (document.fonts?.ready) await document.fonts.ready;
  } catch {}

  // Logo como dataURL (evita cualquier problema en localhost)
  const logoDataUrl = await (async () => {
    try {
      const res = await fetch("/fotos/Logo_EKINE.png");
      const blob = await res.blob();
      return await new Promise((resolve) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  })();

  const nomPac = `${paciente?.nombres || ""} ${paciente?.apellidos || ""}`.trim();

  // Header
  const header = document.createElement("div");
  header.className = "pdf-keep";
  header.innerHTML = `
    <div style="padding:0 0 10px 0;">
      <div style="display:flex; align-items:flex-start; gap:16px;">
        <div style="width:150px;">
          ${
            logoDataUrl
              ? `<img src="${logoDataUrl}" style="width:150px; height:auto;" />`
              : ``
          }
        </div>

        <div style="flex:1; text-align:center;">
          ${profesionalNombre ? `<div style="font-size:12px; color:#223048;">${escapeHtml(profesionalNombre)}</div>` : ""}
          ${direccionEkine ? `<div style="font-size:12px; color:#223048;">${escapeHtml(direccionEkine)}</div>` : ""}
          <div style="margin-top:10px; font-size:15px; font-weight:800; letter-spacing:.6px;">FICHA CLÍNICA</div>
        </div>

        <div style="width:150px;"></div>
      </div>

      <div style="margin-top:14px; border:1px solid #e5e7eb; border-radius:14px; background:#fff; padding:12px 14px;">
        <div style="display:flex; justify-content:space-between; gap:14px; align-items:flex-start;">
          <div style="min-width:0;">
            <div style="font-weight:700; color:#0f172a; font-size:13px; margin-bottom:2px;">
              ${escapeHtml(nomPac || "Paciente")}
            </div>
            <div style="font-size:11px; color:#475569;">${escapeHtml(paciente?.correo || "")}</div>
            ${paciente?.celular ? `<div style="font-size:11px; color:#0f172a; margin-top:6px;">Teléfono: ${escapeHtml(paciente.celular)}</div>` : ""}
          </div>

          <div style="text-align:right; min-width:220px;">
            ${paciente?.t_emergencia ? `<div style="font-size:11px; color:#0f172a;">Emergencia: ${escapeHtml(paciente.t_emergencia)}</div>` : ""}
            ${paciente?.rut ? `<div style="font-size:11px; color:#0f172a; margin-top:6px;">RUT: ${escapeHtml(paciente.rut)}</div>` : ""}
          </div>
        </div>
      </div>
    </div>
  `;
  wrapper.appendChild(header);

  // Clonar contenido tab
  const clone = node.cloneNode(true);
  wrapper.appendChild(clone);

    // ===== FIX TABLAS ANCHAS (Planificación): evita recorte por overflow =====
  wrapper.querySelectorAll(".overflow-x-auto").forEach((el) => {
    el.style.overflow = "visible";
    el.style.overflowX = "visible";
    el.style.overflowY = "visible";
  });

  // Quita min-widths que fuerzan scroll horizontal en PDF
  wrapper.querySelectorAll("table").forEach((t) => {
    t.style.width = "100%";
    t.style.minWidth = "0";
    t.style.tableLayout = "fixed";
  });

  // Asegura que celdas puedan quebrar texto y no “empujen” el ancho
  wrapper.querySelectorAll("th, td").forEach((cell) => {
    cell.style.wordBreak = "break-word";
    cell.style.overflowWrap = "anywhere";
  });

  // Ocultar botones/links del clone
  wrapper.querySelectorAll("button, [role='button'], a").forEach((el) => {
    el.style.display = "none";
  });

  // Reemplazar inputs/textarea/select por divs “cajas” (pero CONSERVANDO el valor)
  wrapper.querySelectorAll("input, textarea, select").forEach((el) => {
    const tag = el.tagName.toLowerCase();

    // ignora file inputs
    if (tag === "input" && (el.type || "").toLowerCase() === "file") {
      el.style.display = "none";
      return;
    }

    let value = "";

    if (tag === "select") {
      const opt = el.options?.[el.selectedIndex];
      value = (opt?.textContent || "").trim();
    } else if (tag === "textarea") {
      value = (el.value || el.textContent || "").toString();
    } else {
      // input
      const t = (el.type || "").toLowerCase();

      if (t === "checkbox") {
        value = el.checked ? "☑" : "☐";
      } else if (t === "radio") {
        value = el.checked ? "◉" : "○";
      } else {
        value = (el.value || "").toString();
      }
    }

    const div = document.createElement("div");
    const isTextarea = tag === "textarea";

    div.className = "pdf-field " + (isTextarea ? "pdf-field-textarea" : "");
    div.style.whiteSpace = "pre-wrap";   // respeta saltos de línea
    div.style.wordBreak = "break-word";

    // si viene vacío, dejamos un espacio para que se vea la “caja”
    div.textContent = value && value.trim().length ? value : " ";

    el.replaceWith(div);
  });


  // Espera 2 frames para layout final
  await new Promise((r) => requestAnimationFrame(r));
  await new Promise((r) => requestAnimationFrame(r));

  // Asegurar que imágenes del wrapper estén cargadas/decodificadas
  const imgs = Array.from(wrapper.querySelectorAll("img"));
  await Promise.all(
    imgs.map(async (img) => {
      try {
        if (img.decode) await img.decode();
        else if (!img.complete) await new Promise((res) => (img.onload = res));
      } catch {}
    })
  );

  // ===== Canvas completo =====
  const canvas = await html2canvas(wrapper, {
    scale: 2,
    backgroundColor: "#ffffff",
    useCORS: true,
    allowTaint: true,
    logging: false,
    scrollX: 0,
    scrollY: 0,
    windowWidth: wrapper.scrollWidth,
  });

  // Limpieza wrapper
  document.body.removeChild(wrapper);

  // ===== Meter canvas a PDF por páginas (SIN cortes visibles) =====
  const imgWmm = usableW;

  // altura en px que corresponde a 1 página (según proporción mm→px del canvas)
  const pageCanvasPxH = Math.floor((usableH * canvas.width) / imgWmm);

  let renderedPx = 0;
  let page = 0;

  while (renderedPx < canvas.height) {
    if (page > 0) pdf.addPage();

    const sliceH = Math.min(pageCanvasPxH, canvas.height - renderedPx);

    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = canvas.width;
    pageCanvas.height = sliceH;

    const ctx = pageCanvas.getContext("2d");

    // fondo blanco para evitar bordes/alpha raros
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

    ctx.drawImage(
      canvas,
      0, renderedPx, canvas.width, sliceH, // src
      0, 0, canvas.width, sliceH           // dst
    );

    const pageImg = pageCanvas.toDataURL("image/jpeg", 0.95);
    const sliceHmm = (sliceH * imgWmm) / canvas.width;

    pdf.addImage(pageImg, "JPEG", margin, margin, imgWmm, sliceHmm);

    renderedPx += sliceH;
    page++;
  }

  pdf.save(filename);
}

function escapeHtml(s = "") {
  return String(s)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

  function Pacientes() {
  const { user } = useAuth();
  const rol = user?.rol || "usuario";
  const userUid = user?.uid || null;

  const [tab, setTab] = useState("evaluacion");

  // ✅ AQUI ADENTRO (no arriba del archivo)
  const [cfgGlobal, setCfgGlobal] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        // OJO: ajusta si tu doc real es otro
        const snap = await getDoc(doc(db, "config_global", "global"));
        if (snap.exists()) setCfgGlobal(snap.data());
      } catch (e) {
        console.error("Error cargando config_global:", e);
      }
    })();
  }, []);

  const createRef = useRef(null);

  const refEval = useRef(null);
  const refPlanif = useRef(null);
  const refEvo = useRef(null);
  const refAlta = useRef(null);

  useEffect(() => {
    if (tab === "crear") {
      // pequeño delay para asegurar render del bloque antes de scrollear
      requestAnimationFrame(() => {
        createRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [tab]);

  const closeCrearPaciente = () => {
    setTab("evaluacion"); // o "editar" si prefieres
    // opcional: subir al inicio al cerrar
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const [search, setSearch] = useState("");
  const debounced = useDebounced(search, 350);

  const [loadingSearch, setLoadingSearch] = useState(false);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);

  const [saving, setSaving] = useState(false);

  const [reservasPaciente, setReservasPaciente] = useState([]);

useEffect(() => {
  if (!selected) {
    setReservasPaciente([]);
    return;
  }

  const cargarReservas = async () => {
    try {

      // traer reservas recientes
      const snap = await getDocs(
        collection(db, "reservas")
      );

      const correo = (
        selected.correo || ""
      ).toLowerCase().trim();

      const rut = (
        selected.rut || ""
      ).trim();

      const uid = (
        selected.uid || ""
      ).trim();

      const nombreCompleto = `${selected.nombres || ""} ${selected.apellidos || ""}`
        .toLowerCase()
        .trim();

      const data = snap.docs
        .map((d) => ({
          id: d.id,
          ...d.data(),
        }))
        .filter((r) => {

          const rEmail = (
            r.email || ""
          ).toLowerCase().trim();

          const rRut = (
            r.rut || ""
          ).trim();

          const rUid = (
            r.uid || ""
          ).trim();

          const rNombre = (
            r.nombre || ""
          ).toLowerCase().trim();

          return (
            (correo && rEmail === correo) ||
            (rut && rRut === rut) ||
            (uid && rUid === uid) ||
            (nombreCompleto &&
              rNombre === nombreCompleto)
          );
        });

      // ordenar desc
      data.sort((a, b) => {
        const da = new Date(
          `${a.fecha}T${a.hora || "00:00"}`
        );

        const db = new Date(
          `${b.fecha}T${b.hora || "00:00"}`
        );

        return db - da;
      });

      setReservasPaciente(data);

    } catch (err) {
      console.error(
        "Error cargando reservas:",
        err
      );
    }
  };

  cargarReservas();
}, [selected]);

  const [form, setForm] = useState({
    subjetivo: "",
    objetivo: "",
    analisis: "",
    plan: "",
    presion: "",
    fc: "",
    temp: "",
    spo2: "",
    diagnostico: "",
    sesionId: "",
    notaId: "",
  });

  const [initialEval, setInitialEval] = useState(EVAL_INICIAL_EMPTY);
  const [alta, setAlta] = useState(ALTA_EMPTY);
  const [loadingInitialEval, setLoadingInitialEval] = useState(false);
  const [loadingAlta, setLoadingAlta] = useState(false);
  const [savingInitialEval, setSavingInitialEval] = useState(false);
  const [savingAlta, setSavingAlta] = useState(false);

  const [sessions, setSessions] = useState([]);
  const [notes, setNotes] = useState([]);
  const [sessionNotes, setSessionNotes] = useState([]);
  const [showEvoAvanzado, setShowEvoAvanzado] = useState(false);
  const [planSesionActual, setPlanSesionActual] = useState(null);
  const [loadingPlanSesion, setLoadingPlanSesion] = useState(false);


const PLAN_EMPTY = useMemo(() => ({
  sesiones: [
    {
      titulo: "Sesión 1",
      rows: Array.from({ length: 10 }).map(() => ({
        ejercicioNum: "",
        ejercicioNombre: "",
        carga: "",
        series: "",
        repeticiones: "",
        rer: "",
        descanso: "",
      })),
    },
  ],
}), []);

const [planificacion, setPlanificacion] = useState(PLAN_EMPTY);
const [selectedPlanId, setSelectedPlanId] = useState("");
const [loadingPlanificacion, setLoadingPlanificacion] = useState(false);
const [savingPlanificacion, setSavingPlanificacion] = useState(false);


const planDocRef = useMemo(() => {
  if (!selected?.uid || !selectedPlanId) return null;
  return doc(db, "usuarios", selected.uid, "planificaciones", selectedPlanId);
}, [selected?.uid, selectedPlanId]);


const savePlanificacion = async () => {
  if (!planDocRef) return;

  setSavingPlanificacion(true);
  try {
    await setDoc(
      planDocRef,
      {
        planId: selectedPlanId,
        ...planificacion,
        actualizadoEn: serverTimestamp(),
      },
      { merge: true }
    );
    alert("Planificación guardada.");
  } catch (e) {
    console.error("Error guardando planificación:", e);
    alert("No se pudo guardar la planificación.");
  } finally {
    setSavingPlanificacion(false);
  }
};

useEffect(() => {
  if (!planDocRef) {
    setPlanificacion(PLAN_EMPTY);
    return;
  }

  const loadPlan = async () => {
    setLoadingPlanificacion(true);
    try {
      const snap = await getDoc(planDocRef);
      if (snap.exists()) {
        const d = snap.data() || {};
        setPlanificacion({
          ...PLAN_EMPTY,
          ...d,
          sesiones:
            Array.isArray(d.sesiones) && d.sesiones.length
              ? d.sesiones
              : PLAN_EMPTY.sesiones,
        });
      } else {
        setPlanificacion(PLAN_EMPTY);
      }
    } catch (e) {
      console.error("Error cargando planificación:", e);
      setPlanificacion(PLAN_EMPTY);
    } finally {
      setLoadingPlanificacion(false);
    }
  };

  loadPlan();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [planDocRef, PLAN_EMPTY]);

  const [newPac, setNewPac] = useState({
    nombres: "",
    apellidos: "",
    correo: "",
    celular: "",
    rut: "",
    t_emergencia: "",
    primerasSesiones: {
      kinesiologia: false,
      entrenamiento: false,
      recovery: false,
      masajes: false,
    },
  });




const genTempPassword = () => {
  const arr = new Uint32Array(4);
  window.crypto.getRandomValues(arr);
  return (
    "Ekine!" +
    Array.from(arr).map((n) => n.toString(16)).join("").slice(0, 12)
  );
};

const crearPaciente = async () => {
  try {
    if (!newPac.nombres || !newPac.apellidos || !newPac.correo) {
      alert("Completa nombres, apellidos y correo.");
      setTab("evaluacion");
      return;
    }

    const emailKey = newPac.correo.toLowerCase().trim();

    // 0) si ya existe doc usuario con uid, no intentes crear auth de nuevo
    const refUser = doc(db, "usuarios", emailKey);
    const snapExist = await getDoc(refUser);

    let uidReal = snapExist.exists() ? snapExist.data()?.uid : null;

    // 1) si no hay uid, crear usuario en Auth SIN afectar sesión admin
    if (!uidReal) {
      const existingSec = getApps().find((a) => a.name === "auth-create-user");
      const secApp = existingSec || initializeApp(firebaseConfig, "auth-create-user");
      const createdNow = !existingSec;

      const secAuth = getAuth(secApp);

      try {
        const tempPass = genTempPassword();
        const cred = await createUserWithEmailAndPassword(secAuth, emailKey, tempPass);
        uidReal = cred.user.uid;
      } catch (err) {
        if (err?.code === "auth/email-already-in-use") {
          const snap2 = await getDoc(refUser);
          if (snap2.exists() && snap2.data()?.uid) {
            uidReal = snap2.data().uid;
          } else {
            alert(
              "Ese correo ya existe en Auth pero no tengo el uid guardado en /usuarios. " +
              "Para resolver este caso necesitas una Cloud Function (Admin SDK) que haga getUserByEmail."
            );
            return;
          }
        } else {
          console.error("Error creando Auth user:", err);
          alert("No se pudo crear la cuenta Auth: " + (err?.message || err));
          return;
        }
      } finally {
        try { await signOut(secAuth); } catch {}
        if (createdNow) {
          try { await deleteApp(secApp); } catch {}
        }
      }
    }

    // 2) Guardar /usuarios/{email} con uid REAL (como registro.jsx)
    await setDoc(
      refUser,
      {
        uid: uidReal,
        correo: emailKey,

        nombres: (newPac.nombres || "").trim(),
        apellidos: (newPac.apellidos || "").trim(),
        rut: (newPac.rut || "").trim(),
        celular: (newPac.celular || "").trim(),
        t_emergencia: (newPac.t_emergencia || "").trim(),

        primerasSesiones: newPac.primerasSesiones || {},

        origen: snapExist.exists() ? "admin_manual_update" : "admin_manual",
        creadoEn: snapExist.exists() ? (snapExist.data()?.creadoEn || serverTimestamp()) : serverTimestamp(),
        actualizadoEn: serverTimestamp(),
      },
      { merge: true }
    );

    // 3) /roles/{uid} igual que registro.jsx
    await setDoc(
      doc(db, "roles", uidReal),
      {
        admin: false,
        worker: false,
        user: true,
        creadoEn: serverTimestamp(),
      },
      { merge: true }
    );

    alert("Paciente creado correctamente.");

    setNewPac({
      nombres: "",
      apellidos: "",
      correo: "",
      celular: "",
      rut: "",
      t_emergencia: "",
      primerasSesiones: {
        kinesiologia: false,
        entrenamiento: false,
        recovery: false,
        masajes: false,
      },
    });
  } catch (err) {
    console.error("Error creando paciente:", err);
    alert("No se pudo crear el paciente.");
  }
};


  const filesRef = useRef(null);
  const sessionNotesUnsubsRef = useRef([]);

  const [modalPlan, setModalPlan] = useState(null);
  const [modalSesiones, setModalSesiones] = useState([]);
  const [modalPagadoReal, setModalPagadoReal] = useState(0);
  const [modalSaldo, setModalSaldo] = useState(0);

  const [modalAccionPlan, setModalAccionPlan] = useState(null);
  const [motivoPlan, setMotivoPlan] = useState("");
  const [loadingAccionPlan, setLoadingAccionPlan] = useState(false);

  const [planes, setPlanes] = useState([]);

const finalizarPlan = async () => {
  if (!modalAccionPlan) return;

  try {
    if (!motivoPlan.trim()) {
      alert("Debes ingresar un motivo.");
      return;
    }

    setLoadingAccionPlan(true);

    await updateDoc(
      doc(db, "planes_usuarios", modalAccionPlan.id),
      {
        estado_plan: "finalizado",
        finalizado_manualmente: true,
        motivo_cierre: motivoPlan.trim(),
        cerrado_por: userUid || null,
        cerrado_en: serverTimestamp(),
      }
    );

    setModalAccionPlan(null);
    setMotivoPlan("");

    alert("Plan finalizado correctamente.");
  } catch (err) {
    console.error(err);
    alert("No se pudo finalizar el plan.");
  } finally {
    setLoadingAccionPlan(false);
  }
};
const anularPlan = async () => {
  if (!modalAccionPlan) return;

  try {
    if (!motivoPlan.trim()) {
      alert("Debes ingresar un motivo.");
      return;
    }

    setLoadingAccionPlan(true);

    await updateDoc(
      doc(db, "planes_usuarios", modalAccionPlan.id),
      {
        estado_plan: "anulado",
        motivo_anulacion: motivoPlan.trim(),
        anulado_por: userUid || null,
        anulado_en: serverTimestamp(),
      }
    );

    setModalAccionPlan(null);
    setMotivoPlan("");

    alert("Plan anulado correctamente.");
  } catch (err) {
    console.error(err);
    alert("No se pudo anular el plan.");
  } finally {
    setLoadingAccionPlan(false);
  }
};

const reactivarPlan = async () => {
  if (!modalAccionPlan) return;

  try {

    setLoadingAccionPlan(true);

    await updateDoc(
      doc(db, "planes_usuarios", modalAccionPlan.id),
      {
        estado_plan: "activo",

        reactivado_por: userUid || null,
        reactivado_en: serverTimestamp(),

        motivo_reactivacion: motivoPlan.trim() || "",

        motivo_cierre: deleteField(),
        motivo_anulacion: deleteField(),
      }
    );

    setModalAccionPlan(null);
    setMotivoPlan("");

    alert("Plan reactivado correctamente.");

  } catch (err) {
    console.error(err);
    alert("No se pudo reactivar el plan.");
  } finally {
    setLoadingAccionPlan(false);
  }
};
  const [movimientosPlan, setMovimientosPlan] = useState([]);

const planesDecorados = useMemo(() => {
  // orden por fecha desc (si no hay, queda 0)
  const ordenados = [...planes].sort((a, b) => {
    const ta =
      (a.creadoEn?.toDate ? a.creadoEn.toDate().getTime() : new Date(a.creadoEn || 0).getTime()) || 0;
    const tb =
      (b.creadoEn?.toDate ? b.creadoEn.toDate().getTime() : new Date(b.creadoEn || 0).getTime()) || 0;
    return tb - ta;
  });

  // contar duplicados por "categoria|variante"
  const counts = new Map();
  ordenados.forEach((p) => {
    const key = `${p.categoria || ""}|${p.variante || ""}`;
    counts.set(key, (counts.get(key) || 0) + 1);
  });

  // asignar índice #n por duplicado
  const seen = new Map();



  return ordenados.map((p) => {
    const total = Number(p.sesiones_totales || 0);
    const usadas = Number(p.usadasReales || 0);
    const reservadas = Number(p.reservadasReales || 0);

    const estadoCalculado = getEstadoPlan({
      ...p,
      usadasReales: usadas,
    });
    const key = `${p.categoria || ""}|${p.variante || ""}`;

    const n = (seen.get(key) || 0) + 1;
    seen.set(key, n);

    const tieneDup = (counts.get(key) || 0) > 1;

    const fecha = fmtFechaPlanShort(p.creadoEn || p.fecha || p.fechaCompra);

    const progreso = `${usadas}/${total || "—"}` + (reservadas ? ` (+${reservadas} res.)` : "");
    const estado = getEstadoPlanLabel(estadoCalculado);
    const sufijo = tieneDup ? ` · #${n}` : "";

    const label = `${p.categoria} — ${p.variante}${sufijo} · ${progreso} · ${estado} · ${fecha}`;

    return {
      ...p,
      _label: label,
      _estadoPlan: estadoCalculado,
    };
  });
}, [planes]);


useEffect(() => {
  if (!selected?.uid) {
    setSelectedPlanId("");
    return;
  }

  if (!selectedPlanId && planesDecorados.length) {
    const preferido = planesDecorados.find((p) => !p._finalizado) || planesDecorados[0];
    setSelectedPlanId(preferido.id);
  }
}, [selected?.uid, planesDecorados, selectedPlanId]);



  const fetchAllPacientes = useCallback(async () => {
    setLoadingSearch(true);
    try {
      // ⚙️ WORKER: solo ver sus pacientes (profesionalId == userUid)
      if (rol === "worker" && userUid) {
        const usuariosSnap = await getDocs(
          query(
            collection(db, "usuarios"),
            where("profesionalId", "==", userUid)
          )
        );

        const list = usuariosSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setResults(list);
        return;
      }

      // ⚙️ ADMIN: ve todos los usuarios con rol user (igual que antes)
      if (rol === "admin") {
        const [rolesUserSnap, rolesAdminSnap, rolesWorkerSnap] = await Promise.all([
          getDocs(query(collection(db, "roles"), where("user", "==", true))),
          getDocs(query(collection(db, "roles"), where("admin", "==", true))),
          getDocs(query(collection(db, "roles"), where("worker", "==", true))),
        ]);

        const userIds = rolesUserSnap.docs.map((d) => d.id);
        const adminIds = rolesAdminSnap.docs.map((d) => d.id);
        const workerIds = rolesWorkerSnap.docs.map((d) => d.id);

        const rolesUserSet = makeSetNormalized(userIds);
        const blockedSet = makeSetNormalized([...adminIds, ...workerIds]);

        const usuariosSnap = await getDocs(collection(db, "usuarios"));
        const allUsers = usuariosSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        const filtrados = allUsers
          .filter((u) => isPacienteFull(u, rolesUserSet, blockedSet))
          // ocultar placeholders raros
          .filter((u) => hasValidCorreo(u) || (u.nombres || "").trim());

        setResults(filtrados);
        return;
      }


      // otros roles: nada
      setResults([]);
    } catch (err) {
      console.error("Error al cargar pacientes:", err);
      setResults([]);
    } finally {
      setLoadingSearch(false);
    }
  }, [rol, userUid]);

  const clearSelection = useCallback(() => {
    setSelected(null);
    setSearch("");
    fetchAllPacientes();
    setTab("evaluacion");
  }, [fetchAllPacientes]);

  // búsqueda
  useEffect(() => {
    (async () => {
      // ✅ Si ya hay paciente seleccionado, no recargues la lista
      if (selected) {
        setResults([]);
        setLoadingSearch(false);
        return;
      }

      if (!debounced || debounced.trim().length < 2) {
        fetchAllPacientes();
        return;
      }

      setLoadingSearch(true);
      try {
        // 🔍 WORKER: buscar SOLO entre sus pacientes
        if (rol === "worker" && userUid) {
          const usuariosSnap = await getDocs(
            query(
              collection(db, "usuarios"),
              where("profesionalId", "==", userUid)
            )
          );

          const allUsers = usuariosSnap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));

          const texto = debounced.toLowerCase();

          const filtrados = allUsers.filter((u) => {
            const correo = loadCorreoKey(u);
            const nombres = (u.nombres || "").toLowerCase();
            const apellidos = (u.apellidos || "").toLowerCase();

            return (
              (correo || "").includes(texto) ||
              nombres.includes(texto) ||
              apellidos.includes(texto)
            );
          });

          setResults(filtrados.slice(0, 12));
          return;
        }

        if (rol === "admin") {
          const [rolesUserSnap, rolesAdminSnap, rolesWorkerSnap] =
            await Promise.all([
              getDocs(query(collection(db, "roles"), where("user", "==", true))),
              getDocs(query(collection(db, "roles"), where("admin", "==", true))),
              getDocs(query(collection(db, "roles"), where("worker", "==", true))),
            ]);

          const rolesUserSet = makeSetNormalized(
            rolesUserSnap.docs.map((d) => d.id)
          );
          const blockedSet = makeSetNormalized([
            ...rolesAdminSnap.docs.map((d) => d.id),
            ...rolesWorkerSnap.docs.map((d) => d.id),
          ]);

          const usuariosSnap = await getDocs(collection(db, "usuarios"));
          const allUsers = usuariosSnap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));

          const texto = debounced.toLowerCase();

          const filtrados = allUsers.filter((u) => {
            if (!isPacienteFull(u, rolesUserSet, blockedSet)) return false;

            const correo = loadCorreoKey(u);
            const nombres = (u.nombres || "").toLowerCase();
            const apellidos = (u.apellidos || "").toLowerCase();

            return (
              (correo || "").includes(texto) ||
              nombres.includes(texto) ||
              apellidos.includes(texto)
            );
          });

          setResults(filtrados.slice(0, 12));
          return;
        }
      } catch (err) {
        console.error("Error en búsqueda de pacientes:", err);
      } finally {
        setLoadingSearch(false);
      }
    })();
  }, [debounced, rol, userUid, fetchAllPacientes, selected]);


  useEffect(() => {
    fetchAllPacientes();
  }, [fetchAllPacientes]);

  // planes del paciente
  useEffect(() => {
    if (!selected?.uid) {
      setPlanes([]);
      return;
    }

    const qPlanes = query(
      collection(db, "planes_usuarios"),
      where("uid", "==", selected.uid)
    );

    const unsub = onSnapshot(qPlanes, async (snap) => {
      const planesData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const final = await Promise.all(
        planesData.map(async (plan) => {
          const qRes = query(
            collection(db, "reservas"),
            where("planId", "==", plan.id)
          );
          const sesSnap = await getDocs(qRes);
          const sesiones = sesSnap.docs.map((d) => d.data());

          const usadas = sesiones.filter(
            (r) =>
              r.estadoSesion === "completada" ||
              r.estadoSesion === "realizada"
          ).length;

          const reservadas = sesiones.filter((r) =>
            ["pendiente", "aprobada"].includes(r.estado)
          ).length;

          const pendientes = Math.max(
            (plan.sesiones_totales || 0) - usadas - reservadas,
            0
          );

          const pagosSesiones = sesiones.reduce(
            (acc, s) => acc + (s.monto_pagado || 0),
            0
          );

          const qMov = query(
            collection(db, "planes_movimientos"),
            where("planId", "==", plan.id)
          );
          const movSnap = await getDocs(qMov);
          const movimientos = movSnap.docs.map((d) => d.data());

          const pagosMov = movimientos
            .filter((m) => m.tipo === "pago")
            .reduce((acc, m) => acc + (m.monto || 0), 0);

          const reembolsosMov = movimientos
            .filter((m) => m.tipo === "reembolso")
            .reduce((acc, m) => acc + (m.monto || 0), 0);

          const pagadoReal = pagosSesiones + pagosMov - reembolsosMov;
          const saldo = Math.max((plan.precio_total || 0) - pagadoReal, 0);

          return {
            ...plan,
            usadasReales: usadas,
            reservadasReales: reservadas,
            pendientesReales: pendientes,
            pagadoReal,
            saldoReal: saldo,
          };
        })
      );

      setPlanes(final);
    });

    return () => unsub();
  }, [selected]);

const abrirModalPlan = async (plan) => {
  try {
    // ✅ Si es un plan, lo correcto es traer por planId (sirve para manuales y normales)
    let sesiones = [];

    if (plan?.id) {
      const qRes = query(
        collection(db, "reservas"),
        where("planId", "==", plan.id)
      );

      const snap = await getDocs(qRes);
      sesiones = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // opcional: si quieres asegurar "solo sesiones desde plan"
      sesiones = sesiones.filter((s) => s.desdePlan === true);
      sesiones.sort((a, b) => {
        const da = new Date(`${a.fecha}T${a.hora || "00:00"}`);
        const db = new Date(`${b.fecha}T${b.hora || "00:00"}`);
        return da - db;
      });
    } else {
      // (por si algún día abres modal para “no-plan”, mantén tu lógica antigua)
      const qRes = query(
        collection(db, "reservas"),
        where("uid", "==", selected.uid)
      );
      const snap = await getDocs(qRes);
      sesiones = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((s) => !s.desdePlan);
    }

    // Movimientos del plan (igual que lo tienes)
    const qMov = query(
      collection(db, "planes_movimientos"),
      where("planId", "==", plan.id)
    );
    const movSnap = await getDocs(qMov);
    const movimientos = movSnap.docs.map((d) => d.data());

    const pagosReservas = sesiones.reduce((acc, s) => acc + (s.monto_pagado || 0), 0);
    const pagosMov = movimientos.filter((m) => m.tipo === "pago").reduce((acc, m) => acc + (m.monto || 0), 0);
    const reembolsosMov = movimientos.filter((m) => m.tipo === "reembolso").reduce((acc, m) => acc + (m.monto || 0), 0);

    const pagadoReal = pagosReservas + pagosMov - reembolsosMov;
    const saldo = Math.max((plan.precio_total || 0) - pagadoReal, 0);

    setModalPlan(plan);
    const sesionesValidas = sesiones.filter(
      (s) =>
        s.estado !== "cancelada" &&
        s.estado !== "rechazada"
    );

    const sesionesOrdenadas = sesiones
      .sort((a, b) => {
        const da = new Date(`${a.fecha}T${a.hora || "00:00"}`);
        const db = new Date(`${b.fecha}T${b.hora || "00:00"}`);
        return da - db;
      })
      .map((s) => {

        // índice SOLO considerando válidas
        const idxValido = sesionesValidas
          .sort((a, b) => {
            const da = new Date(`${a.fecha}T${a.hora || "00:00"}`);
            const db = new Date(`${b.fecha}T${b.hora || "00:00"}`);
            return da - db;
          })
          .findIndex((x) => x.id === s.id);

        return {
          ...s,

          // si está cancelada/rechazada -> sin número
          numeroSesion:
            idxValido >= 0 ? idxValido + 1 : null,

          totalSesiones:
            plan?.sesiones_totales || 0,
        };
      });

    setModalSesiones(sesionesOrdenadas);
    setModalPagadoReal(pagadoReal);
    setModalSaldo(saldo);
  } catch (err) {
    console.error("Error abriendo modal:", err);
  }
};


  const loadMovimientosPlan = async (planId) => {
    try {
      const snap = await getDocs(
        query(
          collection(db, "planes_movimientos"),
          where("planId", "==", planId),
          orderBy("fecha", "desc")
        )
      );
      setMovimientosPlan(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
    }
  };

  // sesiones del paciente
useEffect(() => {
  if (!selected) {
    setSessions([]);
    return;
  }

  const unsubs = [];
  const buckets = {}; // resultados por query

  const mergeAndSet = () => {
    const merged = new Map();
    Object.values(buckets)
      .flat()
      .forEach((r) => merged.set(r.id, r));
    setSessions(Array.from(merged.values()));
  };

  const listen = (key, q) => {
    const unsub = onSnapshot(q, (snap) => {
      buckets[key] = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      mergeAndSet();
    });
    unsubs.push(unsub);
  };

  const correo = (selected.correo || "").toLowerCase().trim();

  // 1) por uid (si en reservas.uid guardas auth uid o email)
  const keysUid = Array.from(
    new Set([selected.uid, selected.docId, correo].filter(Boolean))
  );
  keysUid.forEach((k) => {
    listen(
      `uid:${k}`,
      query(collection(db, "reservas"), where("uid", "==", k))
    );
  });

  // 2) por email (solo si tus reservas manuales guardan campo "email")
  if (correo) {
    listen(
      `email:${correo}`,
      query(collection(db, "reservas"), where("email", "==", correo))
    );
  }

  return () => unsubs.forEach((fn) => fn && fn());
}, [selected]);


  // notas generales del paciente
  useEffect(() => {
    if (!selected?.uid) {
      setNotes([]);
      return;
    }

    const notasRef = collection(db, "usuarios", selected.uid, "fichas");
    const qNotas = query(notasRef, orderBy("fecha", "desc"), limit(50));

    const unsub = onSnapshot(
      qNotas,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setNotes(data);
      },
      (err) => {
        console.error("Error cargando notas generales:", err);
      }
    );

    return () => unsub();
  }, [selected?.uid]);

  // notas por sesión
  useEffect(() => {
    sessionNotesUnsubsRef.current.forEach((fn) => fn && fn());
    sessionNotesUnsubsRef.current = [];
    setSessionNotes([]);

    if (!selected?.uid) return;
    if (!sessions.length) return;

    const uid = selected.uid;

    const unsubs = sessions.map((s) => {
      const notasRef = collection(
        db,
        "usuarios",
        uid,
        "reservasNotas",
        s.id,
        "notas"
      );

      const qNotas = query(notasRef, orderBy("fecha", "desc"), limit(20));

      const unsub = onSnapshot(qNotas, (snap) => {
        const current = snap.docs.map((d) => ({
          id: d.id,
          sesionId: s.id,
          ...d.data(),
        }));

        setSessionNotes((prev) => {
          const others = prev.filter((n) => n.sesionId !== s.id);
          return [...others, ...current];
        });
      });

      return unsub;
    });

    sessionNotesUnsubsRef.current = unsubs;

    return () => {
      unsubs.forEach((fn) => fn && fn());
    };
  }, [selected, sessions]);

  // evaluación inicial y alta
  useEffect(() => {
    if (!selected?.uid) {
      setInitialEval(EVAL_INICIAL_EMPTY);
      setAlta(ALTA_EMPTY);
      return;
    }

    const uid = selected.uid;

    const load = async () => {
      try {
        setLoadingInitialEval(true);
        setLoadingAlta(true);

        const refEval = doc(db, "usuarios", uid, "evaluaciones", "inicial");
        const refAlta = doc(db, "usuarios", uid, "evaluaciones", "alta");

        const [snapEval, snapAlta] = await Promise.all([
          getDoc(refEval),
          getDoc(refAlta),
        ]);

        if (snapEval.exists()) {
          const d = snapEval.data() || {};
          const merged = { ...EVAL_INICIAL_EMPTY, ...d };

          if (!merged.antecedentesClinicos && d.antecedentesClinicos) merged.antecedentesClinicos = d.antecedentesClinicos;
          if (!merged.historiaProblema && d.historiaProblema) merged.historiaProblema = d.historiaProblema;
          if (!merged.examenObservacion && d.examenFisico) merged.examenObservacion = d.examenFisico;

          setInitialEval(merged);
        }

        if (snapAlta.exists()) {
          const d = snapAlta.data() || {};
          const merged = { ...ALTA_EMPTY, ...d };

          if (!merged.fechaAlta && d.fechaAltaTexto) merged.fechaAlta = d.fechaAltaTexto;
          if (!merged.evolucion && d.resumenProceso) merged.evolucion = d.resumenProceso;
          if (!merged.objetivosAlcanzados && d.logros) merged.objetivosAlcanzados = d.logros;
          if (!merged.recomendaciones && d.recomendaciones) merged.recomendaciones = d.recomendaciones;

          setAlta(merged);
        }

      } catch (err) {
        console.error("Error cargando eval/alta:", err);
      } finally {
        setLoadingInitialEval(false);
        setLoadingAlta(false);
      }
    };

    load();
  }, [selected]);

const selectUser = async (u) => {
  try {

    const docId = u.id;

    // intentar obtener uid real
    let uidReal =
      u.uid ||
      u.uidAuth ||
      null;

    // si no existe uid en memoria,
    // recargar doc directo firestore
    if (!uidReal) {
      const snap = await getDoc(
        doc(db, "usuarios", docId)
      );

      if (snap.exists()) {
        const data = snap.data();

        uidReal =
          data.uid ||
          data.uidAuth ||
          null;
      }
    }

    const correo = normEmail(
      u.correo ||
      u.email ||
      (String(docId).includes("@")
        ? docId
        : "")
    );

    setSelected({
      docId,

      // 🔥 ahora sí correcto
      uid: uidReal,

      uidAuth: u.uidAuth || null,

      correo,

      nombres: u.nombres || "",
      apellidos: u.apellidos || "",
      celular: u.celular || "",
      rut: u.rut || "",
      t_emergencia: u.t_emergencia || "",
    });

    setResults([]);
    setSearch("");
    setTab("evaluacion");

  } catch (err) {
    console.error(
      "Error seleccionando paciente:",
      err
    );
  }
};

  const onChangeNote = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onChangeInitial = (e) =>
    setInitialEval((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onChangeAlta = (e) =>
    setAlta((s) => ({ ...s, [e.target.name]: e.target.value }));

  const selectedSessionObj = useMemo(
    () => sessions.find((s) => s.id === form.sesionId) || null,
    [sessions, form.sesionId]
  );

  useEffect(() => {
  const run = async () => {
    setPlanSesionActual(null);

    const ses = selectedSessionObj;
    if (!selected?.uid || !ses?.planId) return;

    setLoadingPlanSesion(true);
    try {
      const sesionesDeEstePlan = sessions
        .filter((x) => x.planId === ses.planId)
        .slice()
        .sort((a, b) => {
          const da = new Date(`${a.fecha} ${a.hora || "00:00"}`);
          const db = new Date(`${b.fecha} ${b.hora || "00:00"}`);
          return da - db;
        });

      const idx = sesionesDeEstePlan.findIndex((x) => x.id === ses.id);
      if (idx < 0) return;

      const refP = doc(db, "usuarios", selected.uid, "planificaciones", ses.planId);
      const snap = await getDoc(refP);
      if (!snap.exists()) return;

      const planData = snap.data() || {};
      const sesPlanificada = planData?.sesiones?.[idx] || null;

      setPlanSesionActual({
        idx: idx + 1,
        total: sesionesDeEstePlan.length,
        data: sesPlanificada,
      });
    } catch (e) {
      console.error("Error cargando planificación para sesión:", e);
    } finally {
      setLoadingPlanSesion(false);
    }
  };

  run();
}, [selected?.uid, selectedSessionObj, sessions]);


  const saveNote = async () => {
    if (!selected?.uid) return;

    const uid = selected.uid;
    const {
      subjetivo,
      objetivo,
      analisis,
      plan,
      presion,
      fc,
      temp,
      spo2,
      diagnostico,
      sesionId,
      notaId,
    } = form;

    if (!subjetivo && !objetivo && !analisis && !plan) {
      alert("Escribe al menos algún campo.");
      return;
    }

    setSaving(true);
    try {
      const basePayload = {
        subjetivo,
        objetivo,
        analisis,
        plan,
        signosVitales: { presion, fc, temp, spo2 },
        diagnostico: diagnostico || "",
        fecha: serverTimestamp(),
      };

      const files = filesRef.current?.files || [];

      if (notaId) {
        let refNota;
        if (sesionId) {
          refNota = doc(
            db,
            "usuarios",
            uid,
            "reservasNotas",
            sesionId,
            "notas",
            notaId
          );
        } else {
          refNota = doc(db, "usuarios", uid, "fichas", notaId);
        }

        await updateDoc(refNota, basePayload);
      } else {
        let refNotas;
        let payload = { ...basePayload, adjuntos: [] };

        if (sesionId) {
          const ses = sessions.find((s) => s.id === sesionId);
          payload.servicio =
            ses?.servicio_raw ||
            ses?.servicio_categoria ||
            ses?.servicio_variante ||
            "";

          refNotas = collection(
            db,
            "usuarios",
            uid,
            "reservasNotas",
            sesionId,
            "notas"
          );
        } else {
          refNotas = collection(db, "usuarios", uid, "fichas");
        }

        const nuevoDoc = await addDoc(refNotas, payload);

        const urls = [];
        for (const f of files) {
          const path = `fichas/${uid}/${nuevoDoc.id}/${Date.now()}_${sanitizeFilename(
            f.name
          )}`;
          const storageRef = ref(storage, path);
          await uploadBytes(storageRef, f);
          const url = await getDownloadURL(storageRef);
          urls.push({ name: f.name, url, path });
        }

        if (urls.length) {
          await updateDoc(nuevoDoc, { adjuntos: urls });
        }
      }

      setForm({
        subjetivo: "",
        objetivo: "",
        analisis: "",
        plan: "",
        presion: "",
        fc: "",
        temp: "",
        spo2: "",
        diagnostico: "",
        sesionId: "",
        notaId: "",
      });

      if (filesRef.current) filesRef.current.value = null;
    } catch (err) {
      console.error("Error guardando nota:", err);
      alert("No se pudo guardar la nota.");
    } finally {
      setSaving(false);
    }
  };

  const saveInitial = async () => {
    if (!selected?.uid) return;
    const uid = selected.uid;

    setSavingInitialEval(true);
    try {
      const refEval = doc(db, "usuarios", uid, "evaluaciones", "inicial");

      await setDoc(
        refEval,
        {
          ...initialEval,
          actualizadoEn: serverTimestamp(),
        },
        { merge: true }
      );

      alert("Evaluación inicial guardada.");
    } catch (err) {
      console.error(err);
      alert("No se pudo guardar.");
    } finally {
      setSavingInitialEval(false);
    }
  };

  const saveAlta = async () => {
    if (!selected?.uid) return;

    const uid = selected.uid;
    setSavingAlta(true);

    try {
      const refAlta = doc(db, "usuarios", uid, "evaluaciones", "alta");
      await setDoc(
        refAlta,
        {
          ...alta,
          actualizadoEn: serverTimestamp(),
        },
        { merge: true }
      );
      alert("Alta guardada.");
    } catch (err) {
      console.error(err);
      alert("No se pudo guardar el alta.");
    } finally {
      setSavingAlta(false);
    }
  };

  const updatePaciente = async (updates) => {
    if (!selected) return alert("Selecciona un paciente");

    try {
      // 🔥 ID real del doc en /usuarios (lo guardamos en selectUser)
      const docId =
        selected.docId || (selected.correo ? selected.correo.trim() : "");

      if (!docId) {
        alert("No se encontró el documento del paciente.");
        return;
      }

      const refU = doc(db, "usuarios", docId);

      const normalizado = normalizePacienteData({
        ...selected,
        ...updates,
      });

      // setDoc + merge => crea si no existe y actualiza si existe
      await setDoc(refU, normalizado, { merge: true });

      alert("Paciente actualizado.");
      setSelected((prev) => ({ ...prev, ...normalizado }));
    } catch (err) {
      console.error("Error actualizando paciente:", err);
      alert("No se pudo actualizar.");
    }
  };

  const combinedRecords = useMemo(() => {
    if (!selected) return [];

    const sesionesMapped = sessions.map((s) => ({
      ...s,
      tipo: "sesion",
      servicio:
        s.servicio_raw ||
        s.servicio_categoria ||
        s.servicio_variante ||
        "Servicio",
      fechaNum: new Date(s.fecha || 0).getTime(),
    }));

    const notasGeneralesMapped = notes.map((n) => ({
      ...n,
      tipo: "nota",
      servicio: "Nota clínica",
      fechaNum: n.fecha?.toDate
        ? n.fecha.toDate().getTime()
        : new Date(n.fecha || 0).getTime(),
    }));

    const notasSesionMapped = sessionNotes.map((n) => {
      const ses = sessions.find((s) => s.id === n.sesionId);
      const servicio =
        n.servicio ||
        ses?.servicio_raw ||
        ses?.servicio_categoria ||
        ses?.servicio_variante ||
        "Sesión";

      const fuenteFecha = n.fecha || ses?.fecha;
      const fechaNum = fuenteFecha?.toDate
        ? fuenteFecha.toDate().getTime()
        : new Date(fuenteFecha || 0).getTime();

      return {
        ...n,
        tipo: "notaSesion",
        servicio,
        fechaNum,
      };
    });

    const all = [
      ...sesionesMapped,
      ...notasGeneralesMapped,
      ...notasSesionMapped,
    ];

    all.sort((a, b) => {
      const A = (a.servicio || "").toLowerCase();
      const B = (b.servicio || "").toLowerCase();
      if (A < B) return -1;
      if (A > B) return 1;
      return b.fechaNum - a.fechaNum;
    });

    return all;
  }, [sessions, notes, sessionNotes, selected]);

  const handleSelectRecord = (item) => {
    if (item.tipo === "sesion") {
      setForm((f) => ({
        ...f,
        sesionId: item.id,
        notaId: "",
      }));
      return;
    }

    if (item.tipo === "nota" || item.tipo === "notaSesion") {
      const v = item.signosVitales || {};
      setForm((f) => ({
        ...f,
        sesionId: item.sesionId || "",
        notaId: item.id,
        subjetivo: item.subjetivo || "",
        objetivo: item.objetivo || "",
        analisis: item.analisis || "",
        plan: item.plan || "",
        presion: v.presion || "",
        fc: v.fc || "",
        temp: v.temp || "",
        spo2: v.spo2 || "",
        diagnostico: item.diagnostico || "",
      }));
    }
  };

const modalStats = useMemo(() => {
  const fechasOrdenadas = [...modalSesiones]
    .filter((s) => s.fecha)
    .sort((a, b) => {
      const da = new Date(`${a.fecha}T${a.hora || "00:00"}`);
      const db = new Date(`${b.fecha}T${b.hora || "00:00"}`);
      return da - db;
    });

  return {
    total: modalPlan?.sesiones_totales || 0,

    realizadas: modalSesiones.filter(
      (s) => s.estado === "realizada"
    ).length,

    reservadas: modalSesiones.filter(
      (s) =>
        s.estado === "aprobada" ||
        s.estado === "pendiente"
    ).length,

    asistio: modalSesiones.filter(
      (s) => s.estadoSesion === "completada"
    ).length,

    pendientes:
      (modalPlan?.sesiones_totales || 0) -
      modalSesiones.filter(
        (s) =>
          s.estadoSesion === "completada" ||
          s.estadoSesion === "realizada"
      ).length -
      modalSesiones.filter(
        (s) =>
          s.estado === "aprobada" ||
          s.estado === "pendiente"
      ).length,

    noAsistio: modalSesiones.filter(
      (s) => s.estadoSesion === "realizada"
    ).length,

    primeraFecha:
      fechasOrdenadas[0]?.fecha || null,

    ultimaFecha:
      fechasOrdenadas[
        fechasOrdenadas.length - 1
      ]?.fecha || null,
  };
}, [modalSesiones, modalPlan]);

  const hoy = new Date();

const sesionesFuturas = modalSesiones.filter((s) => {
  const fecha = new Date(`${s.fecha}T${s.hora || "00:00"}`);
  return fecha >= hoy;
});

const sesionesRealizadas = modalSesiones.filter((s) => {
  const fecha = new Date(`${s.fecha}T${s.hora || "00:00"}`);
  return fecha < hoy;
});

const reservasStats = useMemo(() => {
  const ordenadas = [...reservasPaciente].sort((a, b) => {
    const da = new Date(`${a.fecha}T${a.hora || "00:00"}`);
    const db = new Date(`${b.fecha}T${b.hora || "00:00"}`);
    return db - da;
  });

  const realizadas = reservasPaciente.filter(
    (s) => s.estado === "realizada"
  );

  const reservadas = reservasPaciente.filter(
    (s) =>
      s.estado === "aprobada" ||
      s.estado === "pendiente"
  );

  const canceladas = reservasPaciente.filter(
    (s) => s.estado === "cancelada"
  );

  const noAsistio = reservasPaciente.filter(
    (s) => s.estadoSesion === "realizada"
  );

  const asistio = reservasPaciente.filter(
    (s) => s.estadoSesion === "completada"
  );

  // profesional más frecuente
  const profMap = {};

  reservasPaciente.forEach((s) => {
    const nombre = s.profesionalNombre || "—";
    profMap[nombre] = (profMap[nombre] || 0) + 1;
  });

  const profesionalFrecuente =
    Object.entries(profMap)
      .sort((a, b) => b[1] - a[1])[0]?.[0] || "—";

  return {
    total: reservasPaciente.length,
    realizadas: realizadas.length,
    reservadas: reservadas.length,
    canceladas: canceladas.length,
    noAsistio: noAsistio.length,
    asistio: asistio.length,
    profesionalFrecuente,
    ultimaSesion: ordenadas[0]?.fecha || null,
  };
}, [reservasPaciente]);

const patientHeader = useMemo(() => {
  if (!selected) return null;

  return (
    <div className="w-full bg-white rounded-2xl p-4 shadow mb-4 border border-gray-100 relative">
      <button
        onClick={clearSelection}
        className="absolute right-3 top-3 text-gray-500 hover:text-blue-700 transition text-lg leading-none"
      >
        ✕
      </button>

      <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-4">
        {/* Avatar */}
        <UserCircle2 className="w-14 h-14 text-gray-600" />

        {/* Info */}
        <div className="flex flex-col gap-2">
          {/* Nombre + correo */}
          <div>
            <div className="text-lg font-semibold text-gray-900 leading-tight">
              {selected.nombres} {selected.apellidos}
            </div>
            <div className="text-sm text-gray-500">
              {selected.correo}
            </div>
          </div>

          {/* Datos secundarios */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-700">
            {selected.celular && (
              <div>
                <span className="font-medium text-gray-800">Teléfono:</span>{" "}
                {selected.celular}
              </div>
            )}

            {selected.t_emergencia && (
              <div>
                <span className="font-medium text-gray-800">Emergencia:</span>{" "}
                {selected.t_emergencia}
              </div>
            )}

            {selected.rut && (
              <div>
                <span className="font-medium text-gray-800">RUT:</span>{" "}
                {selected.rut}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}, [selected, clearSelection]);


  return (
  <div className="min-h-screen bg-slate-100/70 px-4 md:px-8 py-6">
    <div className="max-w-[1200px] mx-auto space-y-4 md:space-y-1">
      <h1 className="text-2xl font-bold text-slate-900">Pacientes</h1>
        <p className="text-sm text-slate-600">Evaluación inicial, evolución por sesión, alta, y más.</p>

      {/* Tabs principales de ficha clínica */}
      <div className="border-b pb-2">
        <div className="flex gap-2 overflow-x-auto whitespace-nowrap">
          <TabButton
            active={tab === "evaluacion"}
            onClick={() => setTab("evaluacion")}
            disabled={!selected}
          >
            Evaluación inicial
          </TabButton>
          <TabButton
            active={tab === "planificacion"}
            onClick={() => setTab("planificacion")}
            disabled={!selected}
          >
            Planificación sesiones
          </TabButton>
          <TabButton
            active={tab === "evolucion"}
            onClick={() => setTab("evolucion")}
            disabled={!selected}
          >
            Evolución por sesión
          </TabButton>
          <TabButton
            active={tab === "alta"}
            onClick={() => setTab("alta")}
            disabled={!selected}
          >
            Alta
          </TabButton>
          <TabButton
            active={tab === "reservas"}
            onClick={() => setTab("reservas")}
            disabled={!selected}
          >
            Reservas del paciente
          </TabButton>
          <TabButton
            active={tab === "planes"}
            onClick={() => setTab("planes")}
            disabled={!selected}
          >
            Planes del paciente
          </TabButton>
          <TabButton
            active={tab === "editar"}
            onClick={() => setTab("editar")}
            disabled={!selected}
          >
            Editar datos
          </TabButton>

          {/* 👇 Solo admin ve el tab de crear paciente */}
          {rol === "admin" && (
            <TabButton
              active={tab === "crear"}
              onClick={() => setTab("crear")}
              disabled={false}
            >
              Crear paciente
            </TabButton>
          )}  
        </div>
      </div>

      {rol === "admin" && tab === "crear" && (
        <div ref={createRef} className="bg-white rounded-2xl shadow border border-gray-100 p-4 mb-4 relative">
          <button
            type="button"
            onClick={closeCrearPaciente}
            className="absolute right-3 top-3 w-9 h-9 rounded-full grid place-items-center
                      text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition"
            aria-label="Cerrar crear paciente"
            title="Cerrar"
          >
            ✕
          </button>

          <h2 className="text-lg font-semibold mb-4">Crear nuevo paciente</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <Input
              label="Nombre"
              value={newPac.nombres}
              onChange={(e) => setNewPac((s) => ({ ...s, nombres: e.target.value }))}
            />
            <Input
              label="Apellido"
              value={newPac.apellidos}
              onChange={(e) => setNewPac((s) => ({ ...s, apellidos: e.target.value }))}
            />
            <Input
              label="RUT"
              value={newPac.rut}
              onChange={(e) => setNewPac((s) => ({ ...s, rut: e.target.value }))}
            />
            <Input
              label="Correo"
              value={newPac.correo}
              onChange={(e) => setNewPac((s) => ({ ...s, correo: e.target.value }))}
            />
            <Input
              label="Teléfono"
              value={newPac.celular}
              onChange={(e) => setNewPac((s) => ({ ...s, celular: e.target.value }))}
            />
            <Input
              label="Teléfono de emergencia"
              value={newPac.t_emergencia}
              onChange={(e) => setNewPac((s) => ({ ...s, t_emergencia: e.target.value }))}
            />
          </div>

          <div className="flex justify-end mt-4">
            <button onClick={crearPaciente} className={BTN_PRIMARY}>
              Crear paciente
            </button>
          </div>
        </div>
      )}

      {/* Buscador (NO mostrar en Crear paciente) */}
      {tab !== "crear" && (
        <div className={`${SUBCARD} p-4 mb-2`}>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2 flex-1 border rounded-xl px-3 py-1.5">
              <Search className="w-5 h-5 text-gray-500" />
              <input
                type="text"
                className="flex-1 outline-none text-sm"
                placeholder="Buscar paciente por nombre, apellido o correo…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {loadingSearch && (
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              )}
            </div>

            {selected && (
              <button onClick={clearSelection} className={BTN_SECONDARY}>
                Volver a lista de pacientes
              </button>
            )}
          </div>

          {/* Resultados */}
          {!selected && (
            <div className="mt-3 border-t pt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              {loadingSearch ? (
                <div className="py-4 text-center text-gray-500 text-sm col-span-2">
                  <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Cargando…
                </div>
              ) : results.length === 0 ? (
                <div className="py-4 text-center text-gray-500 text-sm col-span-2">
                  No hay pacientes registrados.
                </div>
              ) : (
                results.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => selectUser(u)}
                    className="text-left p-3 rounded-xl border hover:border-blue-300 transition bg-gray-50 hover:bg-white"
                  >
                    <div className="text-sm font-semibold text-slate-900">
                      {u.nombres || "—"} {u.apellidos || ""}
                    </div>
                    <div className={MUTED + " break-all"}>{u.correo}</div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}


      {/* Encabezado del paciente (oculto en "Crear paciente") */}
      {tab !== "crear" && patientHeader}

      {/* ===================== TAB: EDITAR DATOS ===================== */}
      {selected && tab === "editar" && (
        <div className="bg-white rounded-2xl shadow border border-gray-100 p-4 mb-4">
          <h2 className="text-lg font-semibold mb-3">
            Editar datos del paciente
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            <Input
              label="Nombres"
              value={selected.nombres || ""}
            onChange={(e) =>
              setSelected((s) => ({ ...s, nombres: e.target.value }))
            }
            />

            <Input
              label="Apellidos"
              value={selected.apellidos || ""}
              onChange={(e) =>
                setSelected((s) => ({ ...s, apellidos: e.target.value }))
              }
            />

            <Input
              label="Correo"
              value={selected.correo || ""}
              onChange={(e) =>
                setSelected((s) => ({ ...s, correo: e.target.value }))
              }
            />

            <Input
              label="Celular"
              value={selected.celular || ""}
              onChange={(e) =>
                setSelected((s) => ({ ...s, celular: e.target.value }))
              }
            />

            <Input
              label="RUT"
              value={selected.rut || ""}
              onChange={(e) =>
                setSelected((s) => ({ ...s, rut: e.target.value }))
              }
            />

            <Input
              label="Tel. emergencia"
              value={selected.t_emergencia || ""}
              onChange={(e) =>
                setSelected((s) => ({ ...s, t_emergencia: e.target.value }))
              }
            />
          </div>

          <div className="flex justify-end mt-4">
            <button onClick={() => updatePaciente(selected)} className={BTN_PRIMARY}>
              Guardar cambios
            </button>
          </div>
        </div>
      )}

      {/* ===================== TAB: EVALUACIÓN INICIAL ===================== */}
      {selected && tab === "evaluacion" && (
        <div className="grid grid-cols-1 gap-4">
          <div ref={refEval}>
          <Card title="Evaluación inicial" icon={<FileText className="w-5 h-5" />}>
           <>
            <Section title="Antecedentes y contexto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-1">
                <TextArea
                  label="Antecedentes clínicos"
                  name="antecedentesClinicos"
                  value={initialEval.antecedentesClinicos}
                  onChange={onChangeInitial}
                />
                <TextArea
                  label="Diagnóstico"
                  name="diagnostico"
                  value={initialEval.diagnostico}
                  onChange={onChangeInitial}
                />
              </div>

              <div className="mt-3">
                <TextArea
                  label="Historia del problema (¿qué pasó?, ¿cuándo empezó?, ¿cómo evolucionó?, dolor/EVA)"
                  name="historiaProblema"
                  value={initialEval.historiaProblema}
                  onChange={onChangeInitial}
                />
                <div className="mt-2 text-xs text-slate-500">
                  Tip: aquí pueden escribir EVA y cualquier nota tipo “Alicia/Eva” como guía clínica.
                </div>
              </div>
            </Section>

            <Section title="Hábitos y estilo de vida">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-1">
                <TextArea
                  label="Hábitos (TBQ, OH, drogas, AF)"
                  name="habitos"
                  value={initialEval.habitos}
                  onChange={onChangeInitial}
                />
                <TextArea
                  label="Sueño"
                  name="sueno"
                  value={initialEval.sueno}
                  onChange={onChangeInitial}
                />
                <TextArea
                  label="Alimentación / Hidratación"
                  name="alimentacionHidratacion"
                  value={initialEval.alimentacionHidratacion}
                  onChange={onChangeInitial}
                />
              </div>
            </Section>

            <Section title="Examen físico">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-1">
                <TextArea
                  label="Observación"
                  name="examenObservacion"
                  value={initialEval.examenObservacion}
                  onChange={onChangeInitial}
                />
                <TextArea
                  label="Palpación (dolor, sensibilidad)"
                  name="examenPalpacion"
                  value={initialEval.examenPalpacion}
                  onChange={onChangeInitial}
                />
                <TextArea
                  label="ROM"
                  name="examenROM"
                  value={initialEval.examenROM}
                  onChange={onChangeInitial}
                />
                <TextArea
                  label="Fuerza"
                  name="examenFuerza"
                  value={initialEval.examenFuerza}
                  onChange={onChangeInitial}
                />
                <TextArea
                  label="Pruebas específicas"
                  name="examenPruebasEspecificas"
                  value={initialEval.examenPruebasEspecificas}
                  onChange={onChangeInitial}
                />
              </div>
            </Section>

            <Section title="Objetivos y plan de tratamiento">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-1">
                <TextArea
                  label="Objetivo del paciente"
                  name="objetivoPaciente"
                  value={initialEval.objetivoPaciente}
                  onChange={onChangeInitial}
                />
                <TextArea
                  label="Objetivo terapéutico"
                  name="objetivoTerapeutico"
                  value={initialEval.objetivoTerapeutico}
                  onChange={onChangeInitial}
                />
              </div>

              <div className="mt-3">
                <TextArea
                  label="Plan de tratamiento"
                  name="planTratamiento"
                  value={initialEval.planTratamiento}
                  onChange={onChangeInitial}
                />
              </div>
            </Section>

            <div className="flex justify-end pt-2">
              <button
                onClick={saveInitial}
                disabled={savingInitialEval}
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 disabled:opacity-60 text-sm"
              >
                {savingInitialEval ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Guardar evaluación inicial
              </button>
            </div>
          </>
          </Card>
        </div>
            <div className="flex justify-end">
              <button
                type="button"
                className={BTN_SECONDARY}
                onClick={() =>
                  exportNodeToPDF(refEval.current, `Evaluación_${selected?.nombres || ""}_${selected?.apellidos || ""}.pdf`, {
                    paciente: {
                      nombres: selected?.nombres || "",
                      apellidos: selected?.apellidos || "",
                      correo: selected?.correo || "",
                      celular: selected?.celular || "",
                      rut: selected?.rut || "",
                      t_emergencia: selected?.t_emergencia || "",
                    },
                    profesionalNombre: cfgGlobal?.nombre_profesional || "",     // <-- tu fuente real
                    direccionEkine: cfgGlobal?.ubicacion_texto || "",          // <-- tu fuente real
                    paper: "letter",
                  })
                }
              >
                Descargar
              </button>

          </div>
        </div>
      )}

      {selected && tab === "planificacion" && (
        <div className="grid grid-cols-1 gap-4">
          <div ref={refPlanif}>
          <Card title="Planificación sesiones" icon={<FileText className="w-5 h-5" />}>
            {loadingPlanificacion ? (
              <div className="py-6 text-center text-gray-500">
                <Loader2 className="w-5 h-5 inline animate-spin mr-1" />
                Cargando planificación…
              </div>
            ) : (
              <>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <span className="text-xs text-slate-600">Plan:</span>

                      <select
                        className="bg-white border border-slate-300 rounded-xl px-3 py-2 text-sm"
                        value={selectedPlanId}
                        onChange={(e) => setSelectedPlanId(e.target.value)}
                      >
                        {planesDecorados.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p._label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex flex-col gap-2">

                      <div className="text-xs text-slate-500">
                        La tabla incluye todos los parámetros: ejercicio, carga, series, repeticiones, RER y descanso.
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() =>
                        setPlanificacion((p) => {
                          const nextN = p.sesiones.length + 1;
                          return {
                            ...p,
                            sesiones: [
                              ...p.sesiones,
                              {
                                titulo: `Sesión ${nextN}`,
                                rows: Array.from({ length: 10 }).map(() => ({
                                  ejercicioNum: "",
                                  ejercicioNombre: "",
                                  carga: "",
                                  series: "",
                                  repeticiones: "",
                                  rer: "",
                                  descanso: "",
                                })),
                              },
                            ],
                          };
                        })
                      }
                      className={BTN_SECONDARY}
                    >
                      + Agregar sesión
                    </button>

                    <button
                      type="button"
                      onClick={savePlanificacion}
                      disabled={savingPlanificacion}
                      className={BTN_PRIMARY}
                    >
                      {savingPlanificacion ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Guardar
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {planificacion.sesiones.map((ses, sesIdx) => (
                    <div key={sesIdx} className="border rounded-2xl overflow-hidden bg-white">
                      <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b">
                        <div className="flex items-center justify-between mb-2">
                          <input
                            value={ses.titulo || ""}
                            onChange={(e) =>
                              setPlanificacion((p) => {
                                const sesiones = p.sesiones.slice();

                                sesiones[sesIdx] = {
                                  ...sesiones[sesIdx],
                                  titulo: e.target.value,
                                };

                                return {
                                  ...p,
                                  sesiones,
                                };
                              })
                            }
                            placeholder={`Sesión ${sesIdx + 1}`}
                            className="bg-white border border-slate-300 rounded-lg px-3 py-1 text-sm font-semibold text-slate-800 w-full max-w-[260px]"
                          />
                        </div>

                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              setPlanificacion((p) => {
                                const sesiones = p.sesiones.slice();
                                const target = { ...sesiones[sesIdx] };
                                target.rows = [
                                  ...target.rows,
                                  {
                                    ejercicioNum: "",
                                    ejercicioNombre: "",
                                    carga: "",
                                    series: "",
                                    repeticiones: "",
                                    rer: "",
                                    descanso: "",
                                  },
                                ];
                                sesiones[sesIdx] = target;
                                return { ...p, sesiones };
                              })
                            }
                            className="text-xs font-semibold text-blue-700 hover:underline"
                          >
                            + Ejercicio
                          </button>

                          {planificacion.sesiones.length > 1 && (
                            <button
                              type="button"
                              onClick={() =>
                                setPlanificacion((p) => ({
                                  ...p,
                                  sesiones: p.sesiones.filter((_, i) => i !== sesIdx),
                                }))
                              }
                              className="text-xs font-semibold text-rose-700 hover:underline"
                            >
                              Eliminar sesión
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="p-3">
                        <div className="overflow-x-auto">
                          <table className="min-w-[1180px] w-full text-xs border-separate border-spacing-0">
                            <thead>
                              <tr className="text-slate-600">
                                <th className="text-left p-2 border-b min-w-[260px]">Ejercicio</th>
                                <th className="text-left p-2 border-b min-w-[140px]">Carga</th>
                                <th className="text-left p-2 border-b min-w-[140px]">Series</th>
                                <th className="text-left p-2 border-b min-w-[160px]">Repeticiones</th>
                                <th className="text-left p-2 border-b min-w-[120px]">RER</th>
                                <th className="text-left p-2 border-b min-w-[140px]">Descanso</th>
                                <th className="p-2 border-b min-w-[90px]"></th>
                              </tr>
                            </thead>

                            <tbody>
                              {ses.rows.map((r, rowIdx) => (
                                <tr key={rowIdx} className="align-top">
                                  <td className="p-2 border-b">
                                    <div className="grid grid-cols-[70px_1fr] gap-2">
                                      <input
                                        value={r.ejercicioNum || ""}
                                        onChange={(e) =>
                                          setPlanificacion((p) => {
                                            const sesiones = p.sesiones.slice();
                                            const s2 = { ...sesiones[sesIdx] };
                                            const rows = s2.rows.slice();
                                            rows[rowIdx] = { ...rows[rowIdx], ejercicioNum: e.target.value };
                                            s2.rows = rows;
                                            sesiones[sesIdx] = s2;
                                            return { ...p, sesiones };
                                          })
                                        }
                                        placeholder="#"
                                        className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1"
                                      />
                                      <input
                                        value={r.ejercicioNombre || ""}
                                        onChange={(e) =>
                                          setPlanificacion((p) => {
                                            const sesiones = p.sesiones.slice();
                                            const s2 = { ...sesiones[sesIdx] };
                                            const rows = s2.rows.slice();
                                            rows[rowIdx] = { ...rows[rowIdx], ejercicioNombre: e.target.value };
                                            s2.rows = rows;
                                            sesiones[sesIdx] = s2;
                                            return { ...p, sesiones };
                                          })
                                        }
                                        placeholder="Nombre ejercicio"
                                        className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1"
                                      />
                                    </div>
                                  </td>

                                  <td className="p-2 border-b">
                                    <input
                                      value={r.carga || ""}
                                      onChange={(e) =>
                                        setPlanificacion((p) => {
                                          const sesiones = p.sesiones.slice();
                                          const s2 = { ...sesiones[sesIdx] };
                                          const rows = s2.rows.slice();
                                          rows[rowIdx] = { ...rows[rowIdx], carga: e.target.value };
                                          s2.rows = rows;
                                          sesiones[sesIdx] = s2;
                                          return { ...p, sesiones };
                                        })
                                      }
                                      className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1"
                                    />
                                  </td>

                                  <td className="p-2 border-b">
                                    <input
                                      value={r.series || ""}
                                      onChange={(e) =>
                                        setPlanificacion((p) => {
                                          const sesiones = p.sesiones.slice();
                                          const s2 = { ...sesiones[sesIdx] };
                                          const rows = s2.rows.slice();
                                          rows[rowIdx] = { ...rows[rowIdx], series: e.target.value };
                                          s2.rows = rows;
                                          sesiones[sesIdx] = s2;
                                          return { ...p, sesiones };
                                        })
                                      }
                                      className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1"
                                    />
                                  </td>

                                  <td className="p-2 border-b">
                                    <input
                                      value={r.repeticiones || ""}
                                      onChange={(e) =>
                                        setPlanificacion((p) => {
                                          const sesiones = p.sesiones.slice();
                                          const s2 = { ...sesiones[sesIdx] };
                                          const rows = s2.rows.slice();
                                          rows[rowIdx] = { ...rows[rowIdx], repeticiones: e.target.value };
                                          s2.rows = rows;
                                          sesiones[sesIdx] = s2;
                                          return { ...p, sesiones };
                                        })
                                      }
                                      className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1"
                                    />
                                  </td>

                                  <td className="p-2 border-b">
                                    <input
                                      value={r.rer || ""}
                                      onChange={(e) =>
                                        setPlanificacion((p) => {
                                          const sesiones = p.sesiones.slice();
                                          const s2 = { ...sesiones[sesIdx] };
                                          const rows = s2.rows.slice();
                                          rows[rowIdx] = { ...rows[rowIdx], rer: e.target.value };
                                          s2.rows = rows;
                                          sesiones[sesIdx] = s2;
                                          return { ...p, sesiones };
                                        })
                                      }
                                      className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1"
                                    />
                                  </td>

                                  <td className="p-2 border-b">
                                    <input
                                      value={r.descanso || ""}
                                      onChange={(e) =>
                                        setPlanificacion((p) => {
                                          const sesiones = p.sesiones.slice();
                                          const s2 = { ...sesiones[sesIdx] };
                                          const rows = s2.rows.slice();
                                          rows[rowIdx] = { ...rows[rowIdx], descanso: e.target.value };
                                          s2.rows = rows;
                                          sesiones[sesIdx] = s2;
                                          return { ...p, sesiones };
                                        })
                                      }
                                      className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1"
                                    />
                                  </td>

                                  <td className="p-2 border-b text-right">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setPlanificacion((p) => {
                                          const sesiones = p.sesiones.slice();
                                          const s2 = { ...sesiones[sesIdx] };
                                          s2.rows = s2.rows.filter((_, i) => i !== rowIdx);
                                          sesiones[sesIdx] = s2;
                                          return { ...p, sesiones };
                                        })
                                      }
                                      className="text-xs text-rose-700 hover:underline"
                                    >
                                      Quitar
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>
        </div>
            <div className="flex justify-end">
              <button
                type="button"
                className={BTN_SECONDARY}
                onClick={() =>
                  exportNodeToPDF(refPlanif.current, `Planificación_${selected?.nombres || ""}_${selected?.apellidos || ""}.pdf`, {
                    paciente: {
                      nombres: selected?.nombres || "",
                      apellidos: selected?.apellidos || "",
                      correo: selected?.correo || "",
                      celular: selected?.celular || "",
                      rut: selected?.rut || "",
                      t_emergencia: selected?.t_emergencia || "",
                    },
                    profesionalNombre: cfgGlobal?.nombre_profesional || "",     // <-- tu fuente real
                    direccionEkine: cfgGlobal?.ubicacion_texto || "",          // <-- tu fuente real
                    paper: "letter",
                    orientation: "l",
                  })
                }
              >
                Descargar
              </button>
          </div>
        </div>
      )}


      {/* ===================== TAB: EVOLUCIÓN POR SESIÓN ===================== */}
      {selected && tab === "evolucion" && (
        <div className="grid grid-cols-1 gap-4">
          <div ref={refEvo}>
          {/* Notas clínicas y registro histórico */}
          <div className="space-y-4">
            <Card title="Planificación de la sesión" icon={<FileText className="w-5 h-5" />}>
              {loadingPlanSesion ? (
                <div className="py-4 text-center text-gray-500 text-sm">
                  <Loader2 className="w-4 h-4 inline animate-spin mr-1" />
                  Cargando planificación…
                </div>
              ) : !planSesionActual?.data ? (
                <div className="text-sm text-slate-600">
                  Selecciona una sesión con plan (o no hay planificación guardada para esta sesión).
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-xs text-slate-600">
                    <strong>Sesión:</strong> {planSesionActual.idx} de {planSesionActual.total}
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-[1080px] w-full text-xs border-separate border-spacing-0">
                      <thead>
                        <tr className="text-slate-600">
                          <th className="text-left p-2 border-b">Ejercicio</th>
                          <th className="text-left p-2 border-b">Carga</th>
                          <th className="text-left p-2 border-b">Series</th>
                          <th className="text-left p-2 border-b">Repeticiones</th>
                          <th className="text-left p-2 border-b">RER</th>
                          <th className="text-left p-2 border-b">Descanso</th>
                        </tr>
                      </thead>

                      <tbody>
                        {(planSesionActual.data.rows || []).map((r, i) => (
                          <tr key={i}>
                            <td className="p-2 border-b">
                              {r.ejercicioNum ? `${r.ejercicioNum} — ` : ""}
                              {r.ejercicioNombre || "—"}
                            </td>
                            <td className="p-2 border-b">{r.carga || "—"}</td>
                            <td className="p-2 border-b">{r.series || "—"}</td>
                            <td className="p-2 border-b">{r.repeticiones || "—"}</td>
                            <td className="p-2 border-b">{r.rer || "—"}</td>
                            <td className="p-2 border-b">{r.descanso || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </Card>

            <Card
              title="Nueva nota clínica (Evolución por sesión)"
              icon={<FileText className="w-5 h-5" />}
            >
              {selectedSessionObj && (
                <div className="mt-2 mb-4 p-3 rounded-xl bg-blue-50 border border-blue-200 text-xs sm:text-sm text-blue-900 space-y-1">
                  <div>
                    <strong>Servicio:</strong>{" "}
                    {selectedSessionObj.servicio_raw ||
                      selectedSessionObj.servicio_categoria ||
                      selectedSessionObj.servicio_variante}
                  </div>
                  <div>
                    <strong>Fecha:</strong>{" "}
                    {fmtFecha(selectedSessionObj.fecha)}{" "}
                    {selectedSessionObj.hora
                      ? `— ${selectedSessionObj.hora}`
                      : ""}
                  </div>
                  <div>
                    <strong>Estado:</strong>{" "}
                    {selectedSessionObj.estado ||
                      selectedSessionObj.estadoSesion ||
                      "—"}
                  </div>
                </div>
              )}

                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs text-slate-600">
                    Observaciones (formato ficha)
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowEvoAvanzado((v) => !v)}
                    className="text-xs font-semibold text-blue-700 hover:underline"
                  >
                    {showEvoAvanzado ? "Ocultar avanzado" : "Mostrar avanzado"}
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <TextArea label="Subjetivo" name="subjetivo" value={form.subjetivo} onChange={onChangeNote} />
                  <TextArea label="Objetivo" name="objetivo" value={form.objetivo} onChange={onChangeNote} />
                </div>

                {showEvoAvanzado && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                      <TextArea label="Análisis" name="analisis" value={form.analisis} onChange={onChangeNote} />
                      <TextArea label="Plan" name="plan" value={form.plan} onChange={onChangeNote} />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                      <Input label="Presión" name="presion" value={form.presion} onChange={onChangeNote} />
                      <Input label="FC" name="fc" value={form.fc} onChange={onChangeNote} />
                      <Input label="Temp" name="temp" value={form.temp} onChange={onChangeNote} />
                      <Input label="SpO₂" name="spo2" value={form.spo2} onChange={onChangeNote} />
                    </div>

                    <div className="mt-3">
                      <Input label="Diagnóstico" name="diagnostico" value={form.diagnostico} onChange={onChangeNote} />
                    </div>
                  </>
                )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                <Input
                  label="Presión"
                  name="presion"
                  value={form.presion}
                  onChange={onChangeNote}
                />
                <Input
                  label="FC"
                  name="fc"
                  value={form.fc}
                  onChange={onChangeNote}
                />
                <Input
                  label="Temp"
                  name="temp"
                  value={form.temp}
                  onChange={onChangeNote}
                />
                <Input
                  label="SpO₂"
                  name="spo2"
                  value={form.spo2}
                  onChange={onChangeNote}
                />
              </div>

              <div className="mt-3">
                <Input
                  label="Diagnóstico"
                  name="diagnostico"
                  value={form.diagnostico}
                  onChange={onChangeNote}
                />
              </div>

              <div className="mt-3">
                <label className="text-sm font-medium block mb-1">
                  Adjuntos
                </label>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <input
                    ref={filesRef}
                    type="file"
                    multiple
                    className="block text-xs sm:text-sm"
                  />
                  <div className="text-xs text-gray-500 flex items-center gap-1">
                    <Upload className="w-4 h-4" /> PDF/Imágenes (opcional)
                  </div>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={saveNote}
                  disabled={saving}
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 disabled:opacity-60 text-sm"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Guardar nota
                </button>
              </div>
            </Card>

            <Card
              title="Historial clínico (sesiones y notas)"
              icon={<Activity className="w-5 h-5" />}
            >
              {combinedRecords.length === 0 ? (
                <Empty text="Aún no hay registros clínicos para este paciente." />
              ) : (
                <div className="divide-y divide-gray-200">
                  {combinedRecords.map((item, i) => {
                    // 👉 Cálculo "Sesión X de Y" si viene de un plan
                    let textoSesion = "";
                    if (item.tipo === "sesion" && item.planId) {
                      const sesionesDeEstePlan = combinedRecords
                        .filter(
                          (x) => x.tipo === "sesion" && x.planId === item.planId
                        )
                        .sort((a, b) => {
                          const da = new Date(`${a.fecha} ${a.hora || "00:00"}`);
                          const db = new Date(`${b.fecha} ${b.hora || "00:00"}`);
                          return da - db;
                        });

                      const idx = sesionesDeEstePlan.findIndex(
                        (x) => x.id === item.id
                      );
                      if (idx !== -1) {
                        textoSesion = `Sesión ${idx + 1} de ${
                          sesionesDeEstePlan.length
                        }`;
                      }
                    }

                    return (
                      <div
                        key={i}
                        onClick={() => handleSelectRecord(item)}
                        className="py-3 flex flex-col md:flex-row md:items-center md:justify-between cursor-pointer hover:bg-gray-50 rounded-xl px-2 -mx-2"
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-blue-900">
                            {item.servicio}
                            {item.tipo === "nota" ||
                            item.tipo === "notaSesion"
                              ? " 🩺"
                              : ""}
                          </span>

                          <span className="text-xs text-gray-600">
                            {fmtFecha(item.fecha)} —{" "}
                            {item.tipo === "sesion" ? "Sesión" : "Nota clínica"}
                          </span>

                          {(item.tipo === "nota" ||
                            item.tipo === "notaSesion") && (
                            <span className="text-xs text-gray-700 mt-1 line-clamp-2">
                              {item.subjetivo ||
                                item.objetivo ||
                                item.analisis ||
                                item.plan ||
                                "(sin contenido)"}
                            </span>
                          )}

                          {item.tipo === "sesion" && (
                            <span className="text-xs text-gray-700 mt-1">
                              {textoSesion && <>{textoSesion} · </>}
                              Estado: {item.estado || "pendiente"}{" "}
                              {item.monto_pagado
                                ? `· ${clp(item.monto_pagado)} pagado`
                                : ""}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  className={BTN_SECONDARY}
                  onClick={() =>
                    exportNodeToPDF(refEvo.current, `Evolucion_${selected?.nombres || ""}_${selected?.apellidos || ""}.pdf`, {
                      paciente: {
                        nombres: selected?.nombres || "",
                        apellidos: selected?.apellidos || "",
                        correo: selected?.correo || "",
                        celular: selected?.celular || "",
                        rut: selected?.rut || "",
                        t_emergencia: selected?.t_emergencia || "",
                      },
                      profesionalNombre: cfgGlobal?.nombre_profesional || "",     // <-- tu fuente real
                      direccionEkine: cfgGlobal?.ubicacion_texto || "",          // <-- tu fuente real
                      paper: "letter",
                    })
                  }
                >
                  Descargar
                </button>
              </div>
            </div>
        </div>
      )}

      {/* ===================== TAB: ALTA ===================== */}
      {selected && tab === "alta" && (
        <div ref={refAlta} className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Sidebar (resumen) */}
          <div className="lg:col-span-4">
            <Card title="Resumen rápido" icon={<Activity className="w-5 h-5" />}>
              <>
                <Section title="Resumen alta">
                  <div className="mb-3">
                    <Input
                      label="Fecha alta"
                      name="fechaAlta"
                      value={alta.fechaAlta}
                      onChange={onChangeAlta}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <TextArea
                      label="Evolución"
                      name="evolucion"
                      value={alta.evolucion}
                      onChange={onChangeAlta}
                    />
                    <TextArea
                      label="Objetivos alcanzados"
                      name="objetivosAlcanzados"
                      value={alta.objetivosAlcanzados}
                      onChange={onChangeAlta}
                    />
                  </div>
                </Section>
              </>
            </Card>
          </div>

          {/* Contenido principal */}
          <div className="lg:col-span-8 space-y-4">
            <Card title="Examen físico final" icon={<Activity className="w-5 h-5" />}>
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <TextArea
                    label="Observación"
                    name="examenFinalObservacion"
                    value={alta.examenFinalObservacion}
                    onChange={onChangeAlta}
                  />
                  <TextArea
                    label="Palpación"
                    name="examenFinalPalpacion"
                    value={alta.examenFinalPalpacion}
                    onChange={onChangeAlta}
                  />
                  <TextArea
                    label="ROM"
                    name="examenFinalROM"
                    value={alta.examenFinalROM}
                    onChange={onChangeAlta}
                  />
                  <TextArea
                    label="Fuerza"
                    name="examenFinalFuerza"
                    value={alta.examenFinalFuerza}
                    onChange={onChangeAlta}
                  />
                  <TextArea
                    label="Pruebas específicas"
                    name="examenFinalPruebasEspecificas"
                    value={alta.examenFinalPruebasEspecificas}
                    onChange={onChangeAlta}
                  />
                </div>
              </>
            </Card>

            <Card title="Recomendaciones" icon={<Activity className="w-5 h-5" />}>
              <>
                <TextArea
                  label="Recomendaciones e indicaciones"
                  name="recomendaciones"
                  value={alta.recomendaciones}
                  onChange={onChangeAlta}
                />
              </>
            </Card>

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                className={BTN_SECONDARY}
                onClick={() =>
                  exportNodeToPDF(
                    refAlta.current,
                    `Alta_${selected?.nombres || ""}_${selected?.apellidos || ""}.pdf`,
                    {
                      paciente: {
                        nombres: selected?.nombres || "",
                        apellidos: selected?.apellidos || "",
                        correo: selected?.correo || "",
                        celular: selected?.celular || "",
                        rut: selected?.rut || "",
                        t_emergencia: selected?.t_emergencia || "",
                      },
                      profesionalNombre: cfgGlobal?.nombre_profesional || "",
                      direccionEkine: cfgGlobal?.ubicacion_texto || "",
                      paper: "letter",
                    }
                  )
                }
              >
                Descargar
              </button>

              <button
                onClick={saveAlta}
                disabled={savingAlta}
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 disabled:opacity-60 text-sm"
              >
                {savingAlta ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Guardar alta
              </button>
            </div>
          </div>
        </div>
      )}

        {/* ===================== TAB: RESERVAS DEL PACIENTE ===================== */}
        {selected && tab === "reservas" && (
          <div className="space-y-4">

            {/* STATS */}
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">

              <div className="bg-white border rounded-xl p-3">
                <div className="text-xs text-gray-500">Total</div>
                <div className="text-xl font-semibold">
                  {reservasStats.total}
                </div>
              </div>

              <div className="bg-white border rounded-xl p-3">
                <div className="text-xs text-gray-500">Realizadas</div>
                <div className="text-xl font-semibold">
                  {reservasStats.realizadas}
                </div>
              </div>

              <div className="bg-white border rounded-xl p-3">
                <div className="text-xs text-gray-500">Reservadas</div>
                <div className="text-xl font-semibold">
                  {reservasStats.reservadas}
                </div>
              </div>

              <div className="bg-white border rounded-xl p-3">
                <div className="text-xs text-gray-500">Canceladas</div>
                <div className="text-xl font-semibold">
                  {reservasStats.canceladas}
                </div>
              </div>

              <div className="bg-white border rounded-xl p-3">
                <div className="text-xs text-gray-500">No asistió</div>
                <div className="text-xl font-semibold">
                  {reservasStats.noAsistio}
                </div>
              </div>

              <div className="bg-white border rounded-xl p-3">
                <div className="text-xs text-gray-500">
                  Profesional frecuente
                </div>

                <div className="text-sm font-semibold">
                  {reservasStats.profesionalFrecuente}
                </div>
              </div>

              <div className="bg-white border rounded-xl p-3">
                <div className="text-xs text-gray-500">
                  Última sesión reservada
                </div>

                <div className="text-sm font-semibold">
                  {reservasStats.ultimaSesion
                    ? fmtFechaLocal(reservasStats.ultimaSesion)
                    : "—"}
                </div>
              </div>
            </div>

            {/* LISTA */}
            <div className="bg-white rounded-2xl shadow border border-gray-100 p-4">

              <h2 className="text-lg font-semibold mb-4">
                Historial de reservas
              </h2>

              {reservasPaciente.length === 0 ? (
                <div className="text-sm text-gray-500">
                  No hay reservas para este paciente.
                </div>
              ) : (
                <div className="space-y-3">

                  {[...reservasPaciente]
                    .sort((a, b) => {
                      const da = new Date(`${a.fecha}T${a.hora || "00:00"}`);
                      const db = new Date(`${b.fecha}T${b.hora || "00:00"}`);
                      return db - da;
                    })
                    .map((s, idx) => {

                      const badgePlan = s.desdePlan
                        ? "PLAN"
                        : "INDIVIDUAL";

                      return (
                        <div
                          key={s.id || idx}
                          className="p-4 border rounded-xl bg-gray-50"
                        >

                          <div className="flex items-center justify-between gap-3 mb-2">

                            <div className="font-semibold text-blue-900">
                              {fmtFechaLocal(s.fecha)} — {s.hora}
                            </div>

                            <div className="text-xs font-semibold">
                              {badgePlan}
                            </div>
                          </div>

                          <div className="text-sm text-gray-800">
                            {s.servicio_raw ||
                              s.servicio_categoria ||
                              s.servicio_variante ||
                              "Servicio"}
                          </div>

                          <div className="text-xs text-gray-600 mt-2 space-y-1">

                            <div>
                              <strong>Profesional:</strong>{" "}
                              {s.profesionalNombre || "—"}
                            </div>

                            <div>
                              <strong>Estado reserva:</strong>{" "}
                              {s.estado || "—"}
                            </div>

                            <div>
                              <strong>Estado sesión:</strong>{" "}
                              {getEstadoSesionLabel(s)}
                            </div>

                            <div>
                              <strong>Pago:</strong>{" "}
                              {clp(s.monto_pagado || 0)}
                              {" — "}
                              {s.pago_tipo || "—"}
                            </div>

                            {s.planId ? (
                              <div>
                                <strong>Plan:</strong> Sí
                              </div>
                            ) : (
                              <div>
                                <strong>Plan:</strong> No
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        )}

      {/* ===================== TAB: PLANES DEL PACIENTE ===================== */}
      {selected && tab === "planes" && (
        <div className="bg-white relative rounded-2xl shadow border border-gray-100 p-4 mb-4">
          <h2 className="text-lg font-semibold mb-3">Planes del paciente</h2>

          {planes.length === 0 ? (
            <div className="text-sm text-gray-500">
              Este paciente no tiene planes.
            </div>
          ) : (
            <div className="space-y-4">
              {planes.map((p) => {

                  const valorSesionOriginal =
                    ((p.precio_total || 0) + (p.cupon_descuento || 0)) /
                    (p.sesiones_totales || 1);


                const estadoPlan = getEstadoPlan(p);

                const puedeGestionarPlan =
                  estadoPlan !== "completado";

                return (
                  <div
                    key={p.id}
                    className="p-4 border rounded-xl bg-gray-50 space-y-2"
                  >

                    <div className="flex items-start justify-between gap-3">

                      <div className="flex flex-wrap items-center gap-2">
                        <div className="font-semibold text-blue-900 text-sm sm:text-lg">
                          {p.categoria} — {p.variante} · {p.usadasReales}/{p.sesiones_totales}{" "}
                          {p.usadasReales >= p.sesiones_totales ? "✅" : ""}

                          <span className="text-xs text-slate-500">
                            {" "}
                            · {fmtFechaPlanShort(
                              p.creadoEn || p.fecha || p.fechaCompra
                            )}
                          </span>
                        </div>

                        <span
                          className={`text-[11px] px-2 py-1 rounded-full font-semibold ${getEstadoPlanBadgeClasses(
                            getEstadoPlan(p)
                          )}`}
                        >
                          {getEstadoPlanLabel(getEstadoPlan(p))}
                        </span>
                      </div>

                      {puedeGestionarPlan && (
                        <details className="relative">
                          <summary className="list-none cursor-pointer px-2 py-1 rounded-lg hover:bg-slate-200 text-slate-600 text-lg select-none">
                            ⋮
                          </summary>

                          <div className="absolute right-0 mt-2 w-52 bg-white border border-slate-200 rounded-xl shadow-lg z-20 overflow-hidden">

                            {estadoPlan === "activo" && (
                              <button
                                onClick={() => {
                                  setModalAccionPlan({
                                    ...p,
                                    accion: "finalizar",
                                  });
                                }}
                                className="w-full text-left px-4 py-3 text-sm hover:bg-amber-50 text-amber-800"
                              >
                                Finalizar plan
                              </button>
                            )}

                            {estadoPlan === "activo" &&
                              Number(p.usadasReales || 0) === 0 &&
                              Number(p.reservadasReales || 0) === 0 && (
                                <button
                                  onClick={() => {
                                    setModalAccionPlan({
                                      ...p,
                                      accion: "anular",
                                    });
                                  }}
                                  className="w-full text-left px-4 py-3 text-sm hover:bg-rose-50 text-rose-800"
                                >
                                  Anular plan
                                </button>
                            )}

                            {(estadoPlan === "finalizado" ||
                              estadoPlan === "anulado") && (
                              <button
                                onClick={() => {
                                  setModalAccionPlan({
                                    ...p,
                                    accion: "reactivar",
                                  });
                                }}
                                className="w-full text-left px-4 py-3 text-sm hover:bg-emerald-50 text-emerald-800"
                              >
                                Reactivar plan
                              </button>
                            )}

                          </div>
                        </details>
                      )}
                    </div>

                    <div className="text-xs sm:text-sm text-gray-700 grid md:grid-cols-3 gap-2">
                      <div>
                        <strong>Totales del Plan:</strong> {p.sesiones_totales}
                      </div>

                      <div>
                        <strong>Realizadas:</strong> {p.usadasReales}
                      </div>

                      <div>
                        <strong>Reservas Activas:</strong> {p.reservadasReales}
                      </div>

                      <div>
                        <strong>Pendientes por Reservar:</strong> {p.pendientesReales}
                      </div>

                      <div>
                        <strong>Valor Original:</strong>{" "}
                        {clp(
                          (p.precio_total || 0) +
                          (p.cupon_descuento || 0)
                        )}
                      </div>

                      <div>
                        <strong>Valor Sesión Original:</strong>{" "}
                        {clp(
                          (
                            ((p.precio_total || 0) +
                            (p.cupon_descuento || 0)) /
                            (p.sesiones_totales || 1)
                          )
                        )}
                      </div>

                      <div>
                        <strong>Valor Sesión Final:</strong>{" "}
                        {clp(p.precio_por_sesion)}
                      </div>

                      <div>
                        <strong>Pagado:</strong> {clp(p.pagadoReal)}
                      </div>

                      <div>
                        <strong>Saldo Restante:</strong> {clp(p.saldoReal)}
                      </div>
                    </div>

                    {p.estado_plan === "finalizado" &&
                      p.motivo_cierre && (
                        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                          <strong>Motivo cierre:</strong>{" "}
                          {p.motivo_cierre}
                        </div>
                    )}

                    {p.estado_plan === "anulado" &&
                      p.motivo_anulacion && (
                        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
                          <strong>Motivo anulación:</strong>{" "}
                          {p.motivo_anulacion}
                        </div>
                    )}

                    {p?.cupon_codigo && (
                      <div className="mt-2 text-xs text-gray-600">
                        Cupón aplicado:{" "}
                        <strong>{p.cupon_codigo}</strong>

                        {p.cupon_descuento > 0 && (
                          <>
                            {" "}
                            — Descuento:{" "}
                            <strong>
                              {clp(p.cupon_descuento)}
                            </strong>
                          </>
                        )}
                      </div>
                    )}

                    <button
                      onClick={() => abrirModalPlan(p)}
                      className="px-3 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 text-xs sm:text-sm"
                    >
                      Ver movimientos del plan
                    </button>

                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {modalAccionPlan && (
        <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl p-6 space-y-5 animate-in fade-in zoom-in duration-200">

            <div>
              <h2 className="text-2xl font-bold text-slate-800">
                {modalAccionPlan.accion === "finalizar"
                  ? "Finalizar plan"
                  : modalAccionPlan.accion === "anular"
                  ? "Anular plan"
                  : "Reactivar plan"}
              </h2>

              <p className="text-sm text-slate-500 mt-1">
                Esta acción quedará registrada administrativamente.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-2">
              <div className="font-semibold text-slate-800">
                {modalAccionPlan.categoria}
              </div>

              <div className="text-sm text-slate-600">
                {modalAccionPlan.variante}
              </div>

              <div className="text-sm text-slate-600">
                Sesiones usadas:{" "}
                <strong>{modalAccionPlan.usadasReales}</strong>
              </div>

              <div className="text-sm text-slate-600">
                Sesiones pendientes:{" "}
                <strong>{modalAccionPlan.pendientesReales}</strong>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Motivo
              </label>

              <textarea
                value={motivoPlan}
                onChange={(e) => setMotivoPlan(e.target.value)}
                rows={4}
                placeholder="Escribe el motivo..."
                className="w-full border border-slate-300 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-slate-400 resize-none"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => {
                  setModalAccionPlan(null);
                  setMotivoPlan("");
                }}
                className="px-4 py-2 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-700"
              >
                Cancelar
              </button>

              <button
                disabled={loadingAccionPlan}
                onClick={() => {
                  if (modalAccionPlan.accion === "finalizar") {
                    finalizarPlan();

                  } else if (modalAccionPlan.accion === "anular") {
                    anularPlan();

                  } else {
                    reactivarPlan();
                  }
                }}
                  className={`px-4 py-2 rounded-xl text-white transition
                    ${
                      modalAccionPlan.accion === "finalizar"
                        ? "bg-amber-600 hover:bg-amber-700"
                        : modalAccionPlan.accion === "anular"
                        ? "bg-rose-600 hover:bg-rose-700"
                        : "bg-emerald-600 hover:bg-emerald-700"
                    }`}
              >
                {loadingAccionPlan
                  ? "Guardando..."
                  : modalAccionPlan.accion === "finalizar"
                  ? "Finalizar plan"
                  : modalAccionPlan.accion === "anular"
                  ? "Anular plan"
                  : "Reactivar plan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== MODAL DE SESIONES POR PLAN ===================== */}
      {modalPlan && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-3">
          <div className="
                bg-white
                relative
                w-full
                max-w-5xl
                rounded-2xl
                p-4
                sm:p-6
                shadow-xl
                max-h-[90vh]
                overflow-y-auto
                md:overflow-hidden
                flex
                flex-col
              ">
                <button
                  onClick={() => setModalPlan(null)}
                  className="
                    absolute
                    top-3
                    right-3
                    md:hidden
                    z-10
                    w-8
                    h-8
                    flex
                    items-center
                    justify-center
                    rounded-full
                    bg-gray-100
                    text-gray-600
                    hover:bg-gray-200
                  "
                >
                  ✕
                </button>
            <h2 className="text-lg sm:text-xl font-semibold text-blue-900 mb-3 pr-10">
              Sesiones del plan — {modalPlan.categoria} {modalPlan.variante}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-1 mb-4 text-sm">

              {/* Columna izquierda */}
              <div className="space-y-1">
                <div>
                  <strong>Valor Original:</strong>{" "}
                  {clp(
                    (modalPlan?.precio_total || 0) +
                    (modalPlan?.cupon_descuento || 0)
                  )}
                </div>

                <div>
                  <strong>Valor Final:</strong>{" "}
                  {clp(modalPlan?.precio_total || 0)}
                </div>
              </div>

              {/* Columna derecha */}
              <div className="space-y-1">
                <div>
                  <strong>Valor Sesión Original:</strong>{" "}
                  {clp(
                    (
                      ((modalPlan?.precio_total || 0) +
                        (modalPlan?.cupon_descuento || 0)) /
                      (modalPlan?.sesiones_totales || 1)
                    )
                  )}
                </div>

                <div>
                  <strong>Valor Sesión Final:</strong>{" "}
                  {clp(
                      (modalPlan?.precio_total || 0) /
                      (modalPlan?.sesiones_totales || 1)
                      )}
                </div>
              </div>

              {/* Columna centro */}
              <div className="space-y-1">
                <div>
                  <strong>Pagado Total:</strong>{" "}
                  {clp(modalPlan?.pagadoReal || 0)}
                </div>

                <div>
                  <strong>Saldo Restante:</strong>{" "}
                  {clp(modalPlan?.saldoReal || 0)}
                </div>
              </div>

            </div>

            <div className="space-y-4 mb-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 items-stretch">
                <div className="bg-gray-50 border rounded-xl p-3">
                  <div className="text-xs text-gray-500">Total del Plan</div>
                  <div className="text-lg font-semibold">
                    {modalStats.total}
                  </div>
                </div>

                <div className="bg-gray-50 border rounded-xl p-3">
                  <div className="text-xs text-gray-500">Realizadas</div>
                  <div className="text-lg font-semibold">
                    {modalStats.realizadas}
                  </div>
                </div>

                <div className="bg-gray-50 border rounded-xl p-3">
                  <div className="text-xs text-gray-500">Reservas Activas</div>
                  <div className="text-lg font-semibold">
                    {modalStats.reservadas}
                  </div>
                </div>

                <div className="bg-gray-50 border rounded-xl p-3">
                    <div className="text-xs text-gray-500">
                      Pendientes por Reservar
                    </div>

                    <div className="text-lg font-semibold">
                      {modalStats.pendientes}
                    </div>
                  </div>

                <div className="bg-gray-50 border rounded-xl p-3">
                  <div className="text-xs text-gray-500">Asistió</div>
                  <div className="text-lg font-semibold">
                    {modalStats.asistio}
                  </div>
                </div>

                <div className="bg-gray-50 border rounded-xl p-3">
                  <div className="text-xs text-gray-500">No asistió</div>
                  <div className="text-lg font-semibold">
                    {modalStats.noAsistio}
                  </div>
                </div>
                <div className="bg-gray-50 border rounded-xl p-3 h-full flex flex-col justify-between">
                  <div className="text-xs text-gray-500">
                    Inicio del Plan
                  </div>

                  <div className="text-sm font-semibold">
                    {
                      modalStats.primeraFecha
                        ? fmtFechaLocal(modalStats.primeraFecha)
                        : "—"
                    }
                  </div>
                </div>

                <div className="bg-gray-50 border rounded-xl p-3 h-full flex flex-col justify-between">
                  <div className="text-xs text-gray-500">
                    Última Sesión Reservada
                  </div>

                  <div className="text-sm font-semibold">
                    {
                      modalStats.ultimaFecha
                        ? fmtFechaLocal(modalStats.ultimaFecha)
                        : "—"
                    }
                  </div>
                </div>
              </div>
            </div>

            <div className="
                md:flex-1
                md:overflow-y-auto
                space-y-6
                pr-1
                sm:pr-2
                min-h-0
              ">

              {/* FUTURAS */}
              {sesionesFuturas.length > 0 && (
                <div>
                  <div className="text-sm font-semibold text-slate-700 mb-2">
                    Próximas sesiones
                  </div>

                  <div className="space-y-3">
                    {sesionesFuturas.map((s) => (
                      <div
                        key={s.id}
                        className="p-3 bg-gray-50 rounded-xl border text-xs sm:text-sm"
                      >
                        <div className="font-semibold text-blue-900 mb-1">
                          {
                            s.numeroSesion
                              ? `Sesión ${s.numeroSesion} de ${s.totalSesiones}`
                              : "Sesión cancelada"
                          }
                        </div>

                        <div className="font-medium text-blue-900">
                          {fmtFechaLocal(s.fecha)} — {s.hora}
                        </div>

                        <div className="text-xs text-gray-700">
                          {s.servicio_raw}
                        </div>

                        <div className="text-xs text-gray-600 mt-1">
                          Profesional: {s.profesionalNombre || "—"}
                        </div>

                        <div className="text-xs text-gray-600 mt-1">
                          <strong>Reserva:</strong> {s.estado || "—"}
                        </div>

                        <div className="text-xs text-gray-600">
                          <strong>Sesión:</strong>{" "}
                          {getEstadoSesionLabel(s)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* HISTORIAL */}
              {sesionesRealizadas.length > 0 && (
                <div>
                  <div className="text-sm font-semibold text-slate-700 mb-2">
                    Historial de sesiones
                  </div>

                  <div className="space-y-3">
                    {sesionesRealizadas.map((s) => (
                      <div
                        key={s.id}
                        className="p-3 bg-gray-50 rounded-xl border text-xs sm:text-sm"
                      >
                        <div className="font-semibold text-blue-900 mb-1">
                          {
                            s.numeroSesion
                              ? `Sesión ${s.numeroSesion} de ${s.totalSesiones}`
                              : "Sesión cancelada"
                          }
                        </div>

                        <div className="font-medium text-blue-900">
                          {fmtFechaLocal(s.fecha)} — {s.hora}
                        </div>

                        <div className="text-xs text-gray-700">
                          {s.servicio_raw}
                        </div>

                        <div className="text-xs text-gray-600 mt-1">
                          Profesional: {s.profesionalNombre || "—"}
                        </div>

                        <div className="text-xs text-gray-600 mt-1">
                          <strong>Reserva:</strong> {s.estado || "—"}
                        </div>

                        <div className="text-xs text-gray-600">
                          <strong>Sesión:</strong>{" "}
                          {getEstadoSesionLabel(s)}
                        </div>

                        <div className="text-xs text-gray-600">
                          Pago: {clp(s.monto_pagado)} — {s.pago_tipo}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setModalPlan(null)}
                className="
                  hidden
                  md:inline-flex
                  px-5 py-2 rounded-xl
                  bg-blue-600 text-white
                  hover:bg-blue-700
                "
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
);
}

/* -------------------- UI HELPERS -------------------- */

function Card({ title, icon, children }) {
  return (
    <div className={`${CARD} pdf-keep`}>
      <div className="flex items-center gap-2 px-4 pt-4 pb-3 border-b border-slate-200/70">
        <div className="text-slate-700">{icon}</div>
        <h2 className="font-semibold text-sm sm:text-base text-slate-900">{title}</h2>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Empty({ text }) {
  return (
    <div className="py-8 text-center text-gray-500 text-sm">{text}</div>
  );
}

function Input({ label, ...props }) {
  return (
    <label className="block text-xs sm:text-sm text-slate-700">
      <span className="font-medium block mb-1">{label}</span>
      <input {...props} className={INPUT} />
    </label>
  );
}

function TextArea({ label, ...props }) {
  return (
    <label className="block text-xs sm:text-sm text-slate-700">
      <span className="font-medium block mb-1">{label}</span>
      <textarea rows={4} {...props} className={INPUT + " resize-y"} />
    </label>
  );
}


function TabButton({ active, children, disabled = false, onClick }) {
  const isDisabled = Boolean(disabled);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      className={`px-3 py-1.5 rounded-full text-xs sm:text-sm whitespace-nowrap
        border transition font-semibold
        ${active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}
        ${isDisabled ? "opacity-60 cursor-not-allowed" : ""}`}
    >
      {children}
    </button>
  );
}

function Section({ title, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border rounded-xl mb-3 overflow-hidden pdf-keep">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 text-xs sm:text-sm font-medium"
      >
        <span>{title}</span>
        <span className="text-[11px] text-gray-500">
          {open ? "Ocultar" : "Mostrar"}
        </span>
      </button>
      {open && <div className="px-2 sm:px-3 pb-3 pt-2">{children}</div>}
    </div>
  );
}

/* -------------------- UTILS -------------------- */

function sanitizeFilename(name = "") {
  return name.replace(/[^\w\-.]+/g, "_");
}

function toDateSafe(x) {
  if (!x) return null;

  // Caso Firestore Timestamp
  if (typeof x?.toDate === "function") {
    const d = x.toDate();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  // Caso string YYYY-MM-DD
  if (typeof x === "string" && /^\d{4}-\d{2}-\d{2}$/.test(x)) {
    const [y, m, d] = x.split("-");
    return new Date(y, m - 1, d);
  }

  return null;
}

function fmtFecha(f) {
  if (!f) return "—";
  try {
    let d;

    if (typeof f?.toDate === "function") d = f.toDate();
    else if (typeof f === "string") d = new Date(f);
    else d = new Date(f);

    const dd = d.toLocaleDateString("es-CL", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });

    const hh = d.toLocaleTimeString("es-CL", {
      hour: "2-digit",
      minute: "2-digit",
    });

    return `${dd} ${hh}`;
  } catch {
    return "—";
  }
}

function fmtFechaLocal(fechaStr) {
  if (!fechaStr) return "—";

  try {
    // Evita desfase UTC
    const [y, m, d] = fechaStr.split("-");
    const fecha = new Date(Number(y), Number(m) - 1, Number(d));

    return fecha.toLocaleDateString("es-CL", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return fechaStr;
  }
}

function getEstadoPlan(plan) {
  if (!plan) return "activo";

  if (plan.estado_plan === "anulado") {
    return "anulado";
  }

  if (plan.estado_plan === "finalizado") {
    return "finalizado";
  }

  const usadas = Number(plan.usadasReales || 0);
  const totales = Number(plan.sesiones_totales || 0);

  if (totales > 0 && usadas >= totales) {
    return "completado";
  }

  return "activo";
}

function puedeReservarPlan(plan) {
  const estado = getEstadoPlan(plan);

  return (
    estado !== "finalizado" &&
    estado !== "anulado" &&
    estado !== "completado"
  );
}

function getEstadoPlanBadgeClasses(estado) {
  switch (estado) {
    case "completado":
      return "bg-emerald-100 text-emerald-800 border border-emerald-200";

    case "finalizado":
      return "bg-amber-100 text-amber-800 border border-amber-200";

    case "anulado":
      return "bg-rose-100 text-rose-800 border border-rose-200";

    default:
      return "bg-blue-100 text-blue-800 border border-blue-200";
  }
}

function getEstadoPlanLabel(estado) {
  switch (estado) {
    case "completado":
      return "Completado";

    case "finalizado":
      return "Finalizado manualmente";

    case "anulado":
      return "Anulado";

    default:
      return "Activo";
  }
}

function getEstadoSesionLabel(reserva) {

  // si la reserva fue cancelada/rechazada
  // la sesión debe verse cancelada
  if (
    reserva.estado === "cancelada" ||
    reserva.estado === "rechazada"
  ) {
    return "cancelada";
  }

  // asistió
  if (reserva.estadoSesion === "completada") {
    return "realizada (asistió)";
  }

  // no asistió
  if (reserva.estadoSesion === "realizada") {
    return "realizada (no asistió)";
  }

  // default
  return "pendiente";
}

function calcEdad(fechaRaw) {
  const d = toDateSafe(fechaRaw);
  if (!d || isNaN(d)) return null;

  const today = new Date();

  let e = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();

  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) e--;

  return e;
}

function fmtFechaCorta(fRaw) {
  const d = toDateSafe(fRaw);
  if (!d) return "—";

  return d.toLocaleDateString("es-CL", {
    year: "numeric",
    month: "long",
    day: "2-digit",
  });
}

function fmtFechaPlanShort(ts) {
  if (!ts) return "—";
  try {
    const d = typeof ts?.toDate === "function" ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("es-CL", { year: "numeric", month: "short", day: "2-digit" });
  } catch {
    return "—";
  }
}

function fechaInputValue(fechaRaw) {
  const d = toDateSafe(fechaRaw);
  if (!d) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizePacienteData(data) {
  return {
    nombres: data.nombres || "",
    apellidos: data.apellidos || "",
    correo: data.correo || "",
    celular: data.celular || "",
    rut: data.rut || "",
    t_emergencia: data.t_emergencia || "",
    actualizadoEn: serverTimestamp(),
  };
}



export default Pacientes;
export { Pacientes as PacientesAdminShared };
